import { Fragment, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Form,
    Input,
    message,
    Radio,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    TimePicker,
    Tooltip,
    Typography,
    Upload,
} from 'antd';
import {
    CalendarOutlined,
    CloudUploadOutlined,
    DeleteOutlined,
    DownOutlined,
    EnvironmentOutlined,
    EyeOutlined,
    FileExcelOutlined,
    FileImageOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FileWordOutlined,
    InfoCircleOutlined,
    LockOutlined,
    MailOutlined,
    PhoneOutlined,
    PlusOutlined,
    SolutionOutlined,
    TagsOutlined,
    UpOutlined,
    UserOutlined,
} from '@ant-design/icons';
import TipTapEditor from './TipTapEditor';
import DocumentViewer from './DocumentViewer';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { fetchJsonWithAuth, getActingIdentity } from '../services/authApi';
import { autoValueFor } from '../utils/autoValueRules';
import { AUTH_URL } from '../services/apiConfig';
import { getDropdownValues, searchModuleData } from '../services/moduleDataApi';
import { getFormGroups, createModuleRecord } from '../services/addFormApi';
import { verifyEmail, getModuleDefaultFields } from '../services/adminApi';
import { uploadModuleDocuments } from '../services/documentApi';
import { getAdminClients, getCustomForms, getRoleFormPermissions } from '../services/adminApi';
import { updateRecord } from '../services/detailedViewApi';
import { groupAllowedByRole, fieldAllowedByRole, fieldEditableByRole } from '../utils/roleFormPermissionGate';
import { buildPayload, buildInitialValue, buildRepeatFieldInitial, singleFieldOf, collectFileParts, effectiveDataType, getDeep, toSelectControlValue, usesLabeledSelectValue } from './form/payloadTransformer';
import { computeLinkedSets, findLinkedSet, padLinkedInitialValues } from './form/linkedAddRowGroups';
import useAiActions, { groupActionsByPosition } from './form/useAiActions';
import AiActionButtonGroup from './form/AiActionButtons';
import { applyInputValidator, getKeyPattern } from './form/inputValidator';
import { configuredEmailRule } from './form/emailValidator';
import { securityValidationRule } from './form/inputSecurity';
import { uploadAcceptCategory } from './form/uploadAccept';
import { roleAllowsAction } from '../utils/roleAllowsAction';
import { resolveAfterSubmit, safeNavTarget } from '../utils/afterSubmitNav';
import { submitSuccessMessage, submitErrorMessage } from '../utils/submitMessages';
import { scrollToFirstFormError } from '../utils/scrollToFirstFormError';
import { conditionFieldPath, configuredFieldLabel, conditionMatches as labelConditionMatches } from './form/conditionalFieldLabel';
import AppBreadcrumb from '../components/Breadcrumb';
import ListView from './ListView';
import {
    buildSourceTabs,
    getAvailableSourceActions,
    getSearchSources,
    getSourceActionInstanceKey,
    resolveSearchSource,
    runSourceAction,
    sourceActionAppliesToRecord,
} from '../services/sourceProviderActions';

const { Text } = Typography;
const { TextArea } = Input;
const emptyGroups = [];

const sectionIcons = [
    <UserOutlined />,
    <EnvironmentOutlined />,
    <SolutionOutlined />,
    <InfoCircleOutlined />,
    <FileTextOutlined />,
];

const fieldIcons = {
    email: <MailOutlined className="v1-input-icon" />,
    contactNumber: <PhoneOutlined className="v1-input-icon" />,
    phone: <PhoneOutlined className="v1-input-icon" />,
    currentLocation: <EnvironmentOutlined className="v1-input-icon" />,
    preferredLocation: <EnvironmentOutlined className="v1-input-icon" />,
    skills: <TagsOutlined className="v1-input-icon" />,
    firstName: <UserOutlined className="v1-input-icon" />,
    lastName: <UserOutlined className="v1-input-icon" />,
};

const truthy = (value) => value === true || value === 1 || value === '1';
const editable = (field, moduleName) => (field?.edit === undefined || truthy(field.edit))
    && (!field?.editablePermission || roleAllowsAction(field.editablePermission, moduleName));

function normalizeOptions(options = []) {
    return options.map((option) => {
        if (typeof option === 'string') return { label: option, value: option };
        return {
            label: option.label ?? option.name ?? option.value,
            value: option.value ?? option.id ?? option.name ?? option.label,
        };
    });
}

function namePathFromString(path) {
    return String(path ?? '').split('.').map((part) => part.trim()).filter(Boolean);
}

function siblingFieldPath(absoluteName, key) {
    return Array.isArray(absoluteName) && absoluteName.length > 1
        ? [...absoluteName.slice(0, -1), key]
        : [key];
}

// VerifyFieldButton — admin-configured "Verify" button beside a field
// (field.verifyAction, e.g. "email"). Calls the generic /email-verify
// endpoint and writes the boolean result + a timestamp into two sibling
// fields (verifyResultField/verifyTimestampField), within the same addRow
// row when nested. Not specific to "recruiter" or any module — any field
// can opt in.
function VerifyFieldButton({ form, field, absoluteName }) {
    const [checking, setChecking] = useState(false);
    const verifiedValueRef = useRef(null);
    const resultValue = Form.useWatch(
        field.verifyResultField ? siblingFieldPath(absoluteName, field.verifyResultField) : ['__noop_verify__'],
        { form, preserve: true },
    );
    const currentValue = Form.useWatch(absoluteName, form);

    // A successful result belongs to the exact value that was checked. Existing
    // Edit records hydrate once; later source-value changes invalidate the
    // configured result so the form gate cannot accept a different email.
    useEffect(() => {
        const normalized = String(currentValue ?? '').trim();
        if (resultValue === true && verifiedValueRef.current === null && normalized) {
            verifiedValueRef.current = normalized;
            return;
        }
        if (resultValue === true && verifiedValueRef.current !== null && normalized !== verifiedValueRef.current) {
            if (field.verifyResultField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyResultField), false);
            if (field.verifyTimestampField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyTimestampField), undefined);
            verifiedValueRef.current = null;
        }
    }, [absoluteName, currentValue, field.verifyResultField, field.verifyTimestampField, form, resultValue]);

    if (field.verifyAction !== 'email') return null;

    const runVerify = async () => {
        const email = String(currentValue ?? '').trim();
        if (!email) {
            message.warning('Enter an email first');
            return;
        }
        setChecking(true);
        try {
            const result = await verifyEmail(email);
            verifiedValueRef.current = result.valid ? email : null;
            if (field.verifyResultField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyResultField), result.valid);
            if (field.verifyTimestampField) {
                form.setFieldValue(siblingFieldPath(absoluteName, field.verifyTimestampField), result.valid ? new Date().toISOString() : undefined);
            }
            if (result.valid) message.success('Email verified');
            else message.error(result.reason ? `Invalid email: ${result.reason}` : 'Email could not be verified');
        } catch (err) {
            verifiedValueRef.current = null;
            if (field.verifyResultField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyResultField), false);
            if (field.verifyTimestampField) form.setFieldValue(siblingFieldPath(absoluteName, field.verifyTimestampField), undefined);
            message.error(err?.message || 'Could not verify email');
        } finally {
            setChecking(false);
        }
    };

    if (resultValue === true) {
        return <Text type="success" strong className="v1-verify-field-wrap">{field.verifySuccessLabel || 'Valid'}</Text>;
    }

    return (
        <Button size="small" loading={checking} onClick={runVerify} className="v1-verify-field-wrap">
            {resultValue === false ? (field.verifyRetryLabel || 'Re Verify') : (field.verifyIdleLabel || 'Verify Email')}
        </Button>
    );
}

function ConfiguredFieldLabel({ form, field, siblingPrefix, locked, combined = false }) {
    const watchedValue = Form.useWatch(conditionFieldPath(field?.labelWhen, siblingPrefix), form);
    const label = configuredFieldLabel(field, watchedValue, combined);
    return combined ? label : <FieldLabel field={field} label={label} locked={locked} />;
}

// An ADDITIONAL admin-configured submit button (group.formActions). Same
// validation gate as the primary Create button, plus an optional enabledWhen
// gate that keeps it disabled until a field satisfies the condition (e.g. only
// once "Assign To" is filled). `tone` picks primary vs secondary styling.
function FormActionButton({ form, groups, action, disabled, loading, onFire }) {
    const unvalidated = Form.useWatch(
        (values) => groups.some((group) => {
            if (!group.addRow || !group.requireValidatedField) return false;
            const rows = values?.[group.name] ?? [];
            return rows.length === 0 || rows.some((row) => !row?.[group.requireValidatedField]);
        }),
        { form, preserve: true },
    );
    const gateValue = Form.useWatch(action?.enabledWhen?.field, form);
    const gateOk = !action?.enabledWhen?.field || groupShowIfMatches(action.enabledWhen, gateValue);
    return (
        <Button
            type={action?.tone === 'primary' ? 'primary' : 'default'}
            onClick={onFire}
            disabled={disabled || unvalidated || !gateOk}
            loading={loading}
        >
            {action?.label || 'Submit'}
        </Button>
    );
}

function ValidationGatedSubmitButton({ form, groups, disabled, loading, onClick, children }) {
    const unvalidated = Form.useWatch(
        (values) => groups.some((group) => {
            if (!group.addRow || !group.requireValidatedField) return false;
            const rows = values?.[group.name] ?? [];
            return rows.length === 0 || rows.some((row) => !row?.[group.requireValidatedField]);
        }),
        { form, preserve: true },
    );
    return (
        <Button type="primary" onClick={onClick} disabled={disabled || unvalidated} loading={loading}>
            {children}
        </Button>
    );
}

// PrefillWhenWatcher — sets ITS OWN field's value when a sibling field's value
// matches a configured rule (e.g. contractType="W2" forces employerId to a
// fixed record id). Opposite direction from autofillFrom (which pushes into
// siblings when the changed field is a lookup select) — this field PULLS its
// value from a sibling changing elsewhere. A tiny standalone component (not
// inline logic) because Form.useWatch is a hook and must run in component
// scope, not inside the plain renderField() function.
function PrefillWhenWatcher({ form, rules, watchPath, targetPath }) {
    const watched = Form.useWatch(watchPath, form);
    useEffect(() => {
        const match = rules.find((r) => String(watched ?? '') === String(r.value ?? ''));
        if (match) form.setFieldValue(targetPath, match.setValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watched]);
    return null;
}

// Group-level showIf: the whole group renders only when the referenced field's
// live value satisfies the condition (same operators as field-level showIf).
// Kept as a module-level helper so both AddFormV1 and EditFormV1 evaluate it
// identically.
function groupShowIfMatches(showIf, value) {
    if (!showIf?.field) return true;
    const actual = String(value ?? '');
    const expected = String(showIf.value ?? '');
    const list = () => expected.split(',').map((v) => v.trim());
    switch (showIf.operator) {
        case 'eq': return actual === expected;
        case 'neq': return actual !== expected;
        case 'in': return list().includes(actual);
        case 'notIn': return !list().includes(actual);
        case 'truthy': return truthy(value);
        case 'falsy': return !truthy(value);
        case 'empty':
        case 'isEmpty':
            return value === undefined || value === null || actual.trim() === '' || (Array.isArray(value) && value.length === 0);
        case 'notEmpty':
            return !(value === undefined || value === null || actual.trim() === '' || (Array.isArray(value) && value.length === 0));
        default: return true;
    }
}

// A group with a `showIf` renders through this gate so its visibility reacts to
// the referenced field changing. `render` is only invoked when visible, so a
// hidden group's fields never register (nothing to validate or submit) — exactly
// like a hidden field-level showIf. Groups without showIf bypass this entirely.
function GroupShowIfGate({ form, showIf, render }) {
    const watched = Form.useWatch(showIf.field, form);
    if (!groupShowIfMatches(showIf, watched)) return null;
    return render();
}

function getNestedProp(obj, dotPath) {
    return String(dotPath ?? '').split('.').reduce((cur, k) => cur?.[k], obj);
}

function hasMeaningfulValue(value) {
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0 && value.some(hasMeaningfulValue);
    if (typeof value === 'object') return Object.values(value).some(hasMeaningfulValue);
    return true;
}

function extractGroups(formGroups) {
    if (Array.isArray(formGroups)) return formGroups;
    if (Array.isArray(formGroups?.groups)) return formGroups.groups;
    if (Array.isArray(formGroups?.data)) return formGroups.data;
    if (Array.isArray(formGroups?.data?.groups)) return formGroups.data.groups;
    return emptyGroups;
}

function normalizeGroups(formGroups = [], moduleName, rolePerms, defaultFieldSet) {
    const dset = defaultFieldSet ?? new Set();
    return [...extractGroups(formGroups)]
        .filter((group) => !truthy(group.hideGroup) && groupAllowedByRole(rolePerms, group.name))
        .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
        .map((group) => ({
            ...group,
            fields: [...(group.fields ?? [])]
                // A "default field" (Form Groups → Default Fields, OTP-gated) is
                // ALWAYS shown + required regardless of its own show flag, so it
                // survives the hidden-field filter below and is forced show/req in
                // the map that follows.
                .map((field) => (
                    dset.has(field.field) ? { ...field, show: 1, req: 1 } : field
                ))
                // Drop blank-keyed fields (a stray admin-config row can't render or
                // submit), hidden fields, any client-scope "gate" field (rendered as
                // the top "Select Client" picker, never as a body field), any field
                // whose visiblePermission the current role doesn't hold, and any
                // field this role's Form Configuration (Role Configure → Form,
                // derived from this same Form Groups config) has turned off.
                .filter((field) =>
                    String(field.field ?? '').trim() !== ''
                    && (field.show === undefined || truthy(field.show))
                    && field.showOnAdd !== false
                    && !truthy(field.clientScope)
                    && (!field.visiblePermission || roleAllowsAction(field.visiblePermission, moduleName))
                    && fieldAllowedByRole(rolePerms, group.name, field.field))
                // Role "disable": a group/field the role has marked non-editable
                // still renders but read-only. FieldControl honours field.disabled.
                .map((field) => (
                    fieldEditableByRole(rolePerms, group.name, field.field)
                        ? field
                        : { ...field, disabled: true }
                ))
                .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0)),
        }))
        .filter((group) => group.fields.length);
}

// pickPrefillValue reads a field's initial value from a prefill SOURCE record,
// honouring the admin-config `prefillFrom` mapping FIRST (so a target field can
// inherit from a differently-named source key — e.g. submission.totalExperience
// ← candidate.yearOfExperience), then falling back to field.field and payloadKey.
// Tolerates both flat dotted keys (the getFormGroups flat record) and nested
// objects (addRow rows). Entirely config-driven — no field-name logic here.
// Flattens a form-groups response (scalar fields with embedded values) into a
// plain { fieldKey: value } record. Shared by the default prefill source and
// the cross-module context prefill (field.prefillFromModule).
function flatRecordFromGroups(groups) {
    if (!Array.isArray(groups)) return null;
    const rec = {};
    groups.forEach(group => {
        if (group.addRow) return;
        (group.fields ?? []).forEach(field => {
            if (!field.field || field.value === undefined || field.value === null) return;
            rec[field.field] = field.value;
            if (field.payloadKey && field.payloadKey !== field.field) {
                rec[field.payloadKey] = field.value;
            }
        });
    });
    return Object.keys(rec).length > 0 ? rec : null;
}

function pickPrefillValue(record, field) {
    if (!record || !field) return undefined;
    for (const path of [field.prefillFrom, field.field, field.payloadKey]) {
        if (!path) continue;
        let v = record[path];
        if (v === undefined || v === null) v = getDeep(record, path);
        if (v !== undefined && v !== null) return v;
    }
    return undefined;
}

// Convert a stored file-reference object to an Ant Design fileList entry so
// file fields pre-show previously uploaded files when prefilling. Handles:
//   resume:   { location, uploadedFileName, uniqueName }
//   passport: { passportCopyLocation, passportUploadName, … }
//   visa/alternateVisa: same *CopyLocation / *UploadName pattern
function toFileListValue(value) {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== 'object') return [];
    const name =
        value.uploadedFileName ??
        value.name ??
        Object.entries(value).find(([k]) => k.endsWith('UploadName'))?.[1] ??
        '';
    const url =
        value.location ??
        value.url ??
        Object.entries(value).find(([k]) => k.endsWith('CopyLocation'))?.[1] ??
        '';
    if (!name && !url) return [];
    return [{ uid: url || name || '-1', name: name || 'file', status: 'done', url, existing: true }];
}

const empty = (value) => value === undefined || value === null || value === '';

const REGION_OPTIONS = [
    { label: 'US', value: 'US' },
    { label: 'UK', value: 'UK' },
    { label: 'India', value: 'INDIA' },
];

// Strip HTML tags and decode common entities — used so min/max validation
// checks visible character count, not raw markup length.
const stripHtml = (html) =>
    String(html)
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim();

const numberRangeRule = ({ label, message, value, compare, text }) => ({
    validator: (_, input) => {
        if (empty(input)) return Promise.resolve();
        return compare(Number(input), Number(value))
            ? Promise.resolve()
            : Promise.reject(new Error(message ?? `${label} ${text} ${value}`));
    },
});

function getComparableDateTime(value) {
    if (!value) return null;

    if (typeof value.valueOf === 'function') {
        const time = value.valueOf();
        return Number.isNaN(time) ? null : time;
    }

    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
}

function getComparableDateDay(value) {
    if (!value) return null;
    const date = dayjs(value);
    return date.isValid() ? date.startOf('day').valueOf() : null;
}

// Time-of-day (TimePicker) values share a day, so comparing via valueOf()/startOf('day')
// makes every time equal — compare minutes-since-midnight instead.
function getComparableTimeOfDay(value) {
    if (!value) return null;
    const time = dayjs(value);
    return time.isValid() ? time.hour() * 60 + time.minute() : null;
}

function getSiblingFieldName(currentName, compareField) {
    const compareParts = String(compareField).split('.').filter(Boolean);
    if (!Array.isArray(currentName)) return compareParts.length > 1 ? compareParts : compareField;

    if (compareParts.length > 1) {
        // A DB rule may use `endDate` or `work_experience.endDate`. Preserve
        // the current Form.List row and avoid repeating the group segment.
        if (currentName.length >= 3 && typeof currentName[1] === 'number') {
            const listPrefix = currentName.slice(0, 2);
            const relativeParts = compareParts[0] === String(currentName[0])
                ? compareParts.slice(1)
                : compareParts;
            return [...listPrefix, ...relativeParts];
        }
        return compareParts;
    }

    const nextName = [...currentName];
    nextName[nextName.length - 1] = compareParts[0];
    return nextName;
}

const crossFieldNumberRule = ({ label, message, value, rule, form, name, compare, text }) => ({
    validator: async (_, input) => {
        const compareField = value ?? rule.compareField ?? rule.field;
        if (!compareField || empty(input)) return Promise.resolve();
        const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
        if (empty(compareValue)) return Promise.resolve();
        const inputNumber = Number(input);
        const compareNumber = Number(compareValue);
        if (Number.isNaN(inputNumber) || Number.isNaN(compareNumber)) return Promise.resolve();
        return compare(inputNumber, compareNumber)
            ? Promise.resolve()
            : Promise.reject(new Error(message ?? `${label} ${text} ${compareField}`));
    },
});

const validationRulesByType = {
    noPastDate: ({ label, message }) => ({
        validator: (_, value) => {
            if (empty(value)) return Promise.resolve();
            const time = getComparableDateTime(value);
            if (time === null) return Promise.resolve();
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return time < today.getTime()
                ? Promise.reject(new Error(message ?? `${label} cannot be a past date`))
                : Promise.resolve();
        },
    }),
    noFutureDate: ({ label, message }) => ({
        validator: (_, value) => {
            if (empty(value)) return Promise.resolve();
            const time = getComparableDateTime(value);
            if (time === null) return Promise.resolve();
            const endOfDay = new Date();
            endOfDay.setHours(23, 59, 59, 999);
            return time > endOfDay.getTime()
                ? Promise.reject(new Error(message ?? `${label} cannot be a future date`))
                : Promise.resolve();
        },
    }),
    minAge: ({ label, message, value }) => ({
        validator: (_, input) => {
            if (empty(input)) return Promise.resolve();
            const time = getComparableDateTime(input);
            if (time === null) return Promise.resolve();
            const years = Number(value ?? 18);
            const cutoff = new Date();
            cutoff.setFullYear(cutoff.getFullYear() - years);
            cutoff.setHours(23, 59, 59, 999);
            return time > cutoff.getTime()
                ? Promise.reject(new Error(message ?? `Must be at least ${years} years old`))
                : Promise.resolve();
        },
    }),
    dateAfterField: ({ field, label, message, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareField = value ?? rule.compareField ?? rule.field;
            if (!compareField) return Promise.resolve();

            const startFieldName = getSiblingFieldName(name, compareField);
            const startValue = form.getFieldValue(startFieldName);

            // Non-mandatory case:
            // If either date is empty, don't block submit.
            if (empty(startValue) || empty(input)) {
                return Promise.resolve();
            }

            const startTime = getComparableDateDay(startValue);
            const endTime = getComparableDateDay(input);

            if (startTime === null || endTime === null) {
                return Promise.resolve();
            }

            if (endTime < startTime) {
                return Promise.reject(
                    new Error(message ?? `${label} should not be before Start Date`)
                );
            }

            return Promise.resolve();
        },
    }),
    dateBeforeField: ({ label, message, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareField = value ?? rule.compareField ?? rule.field;
            if (!compareField || empty(input)) return Promise.resolve();
            const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
            if (empty(compareValue)) return Promise.resolve();
            const inputTime = getComparableDateDay(input);
            const compareTime = getComparableDateDay(compareValue);
            if (inputTime === null || compareTime === null || inputTime <= compareTime) return Promise.resolve();
            return Promise.reject(new Error(message ?? `${label} must be on or before ${compareField}`));
        },
    }),
    timeAfterField: ({ label, message, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareField = value ?? rule.compareField ?? rule.field;
            if (!compareField || empty(input)) return Promise.resolve();
            const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
            if (empty(compareValue)) return Promise.resolve();
            const inputTime = getComparableTimeOfDay(input);
            const compareTime = getComparableTimeOfDay(compareValue);
            if (inputTime === null || compareTime === null || inputTime > compareTime) return Promise.resolve();
            return Promise.reject(new Error(message ?? `${label} must be after ${compareField}`));
        },
    }),
    timeBeforeField: ({ label, message, value, rule, form, name }) => ({
        validator: async (_, input) => {
            const compareField = value ?? rule.compareField ?? rule.field;
            if (!compareField || empty(input)) return Promise.resolve();
            const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
            if (empty(compareValue)) return Promise.resolve();
            const inputTime = getComparableTimeOfDay(input);
            const compareTime = getComparableTimeOfDay(compareValue);
            if (inputTime === null || compareTime === null || inputTime < compareTime) return Promise.resolve();
            return Promise.reject(new Error(message ?? `${label} must be before ${compareField}`));
        },
    }),
    greaterThanField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input >= other, text: 'must be greater than or equal to' }),
    strictGreaterThanField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input > other, text: 'must be greater than' }),
    strictLessThanField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input < other, text: 'must be less than' }),
    lessThanOrEqualField: (args) => crossFieldNumberRule({ ...args, compare: (input, other) => input <= other, text: 'must be less than or equal to' }),
    maxFieldWithFallback: ({ label, message, value, rule, form, name }) => ({
        validator: async (_, input) => {
            if (empty(input)) return Promise.resolve();
            const fieldNames = String(value ?? rule.compareFields ?? '').split(',').map((item) => item.trim()).filter(Boolean);
            const selectedField = fieldNames.find((fieldName) => !empty(form.getFieldValue(getSiblingFieldName(name, fieldName))));
            if (!selectedField) return Promise.resolve();
            const maximum = Number(form.getFieldValue(getSiblingFieldName(name, selectedField)));
            const inputNumber = Number(input);
            if (Number.isNaN(maximum) || Number.isNaN(inputNumber) || inputNumber <= maximum) return Promise.resolve();
            return Promise.reject(new Error(message ?? `${label} must not exceed ${selectedField}`));
        },
    }),
    required: ({ field, message }) => ({
        validator: (_, value) => {
            let isEmpty = value === undefined || value === null || value === ''
                || (Array.isArray(value) && value.length === 0);
            if (!isEmpty && field?.type === 'text-editor') {
                isEmpty = stripHtml(value) === '';
            }
            return isEmpty
                ? Promise.reject(new Error(message ?? 'Mandatory Field'))
                : Promise.resolve();
        },
    }),
    email: ({ label, message }) => configuredEmailRule(label, message),
    url: ({ label, message }) => ({ type: 'url', message: message ?? `Enter valid ${label}` }),
    number: ({ label, message }) => ({ pattern: /^[0-9]+$/, message: message ?? `${label} must be a number` }),
    phone: ({ message }) => ({ pattern: /^[0-9]{10}$/, message: message ?? 'Phone number must contain exactly 10 digits' }),
    len: ({ label, message, value }) => ({ len: Number(value), message: message ?? `${label} must be ${value} characters` }),
    pattern: ({ label, message, rule }) => ({
        pattern: new RegExp(rule.pattern ?? rule.value, rule.flags),
        message: message ?? `Enter valid ${label}`,
    }),
    min: ({ field, label, message, value }) => (
        field.type === 'number'
            ? numberRangeRule({ label, message, value, compare: (input, min) => input >= min, text: 'must be at least' })
            : {
                validator: (_, input) => {
                    if (input == null || input === '') return Promise.resolve();
                    const content = field.type === 'text-editor' ? stripHtml(input) : String(input);
                    if (content === '') return Promise.resolve();
                    return content.length >= Number(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error(message ?? `${label} must be at least ${value} characters`));
                },
            }
    ),
    max: ({ field, label, message, value }) => (
        field.type === 'number'
            ? numberRangeRule({ label, message, value, compare: (input, max) => input <= max, text: 'must not exceed' })
            : {
                validator: (_, input) => {
                    if (input == null || input === '') return Promise.resolve();
                    const content = field.type === 'text-editor' ? stripHtml(input) : String(input);
                    if (content === '') return Promise.resolve();
                    return content.length <= Number(value)
                        ? Promise.resolve()
                        : Promise.reject(new Error(message ?? `${label} must not exceed ${value} characters`));
                },
            }
    ),
};

function validationToRule(validation, field, form, name) {
    const rule = typeof validation === 'string' ? { type: validation } : validation;
    const type = rule.type ?? rule.rule ?? rule.name;
    const value = rule.value ?? rule.length ?? rule.min ?? rule.max;
    const label = field.label ?? field.field ?? 'Field';
    const factory = validationRulesByType[type];

    return factory
        ? factory({ field, label, message: rule.message, rule, value, form, name })
        : rule;
}

function getSiblingFieldPath(name, siblingField) {
    return Array.isArray(name) && name.length > 1
        ? [...name.slice(0, -1), siblingField]
        : [siblingField];
}

// A field is required either via the simple "req" toggle or via a "required"
// entry in the admin-configured validations list — the Form.Item asterisk and
// the addRow "at least one row" check must honour both, not just field.req.
function isFieldRequired(field) {
    if (truthy(field?.req)) return true;
    const apiRules = field?.validations ?? field?.validation ?? field?.rules ?? [];
    return apiRules.some((rule) => (typeof rule === 'string' ? rule : rule?.type) === 'required');
}

function getRules(field, form, name, skipRequired = false) {
    const label = field.label ?? field.field ?? 'Field';
    const apiRules = field.validations ?? field.validation ?? field.rules ?? [];
    const hasApiRequired = apiRules.some((rule) => (typeof rule === 'string' ? rule : rule?.type) === 'required');
    const hasApiEmail = apiRules.some((rule) => (typeof rule === 'string' ? rule : rule?.type) === 'email');
    const rules = [];

    if (!skipRequired && truthy(field.req) && !hasApiRequired) {
        // A field disabled via disabledByField (e.g. endDate while
        // currentlyPursuing is checked) must not block submit with a
        // "required" error the user has no way to satisfy.
        if (field.disabledByField) {
            rules.push({
                validator: (_, input) => {
                    const disabledNow = truthy(form.getFieldValue(getSiblingFieldPath(name, field.disabledByField)));
                    if (disabledNow) return Promise.resolve();
                    return (input === undefined || input === null || input === '')
                        ? Promise.reject(new Error('Mandatory Field'))
                        : Promise.resolve();
                },
            });
        } else {
            rules.push({ required: true, message: 'Mandatory Field' });
        }
    }
    if ((field.type === 'email' || field.formatter === 'email') && !hasApiEmail) {
        rules.push(configuredEmailRule(label));
    }
    // Number fields are whole-number-only unless the admin marks them decimal
    // (e.g. a commission of 5.5). Both patterns still reject any non-numeric
    // character, so "float only" stays enforced either way.
    if (field.type === 'number') {
        rules.push(truthy(field.decimal)
            ? { pattern: /^\d+(\.\d+)?$/, message: `${label} must be a number` }
            : { pattern: /^[0-9]+$/, message: `${label} must be a number` });
    }

    return [
        ...rules,
        ...apiRules.map((rule) => validationToRule(rule, field, form, name)),
        securityValidationRule(field),
    ];
}

function getValidationValue(field, type) {
    const rules = field.validations ?? field.validation ?? field.rules ?? [];
    const rule = rules.find((item) => (typeof item === 'string' ? item : item?.type) === type);
    if (!rule || typeof rule === 'string') return undefined;
    return rule.value ?? rule.length ?? rule[type];
}

function getInputLimits(field) {
    const len = field.len ?? getValidationValue(field, 'len');
    const min = field.min ?? getValidationValue(field, 'min');
    const max = field.max ?? getValidationValue(field, 'max');

    if (field.type === 'number') {
        return {
            min: min ?? undefined,
            max: max ?? undefined,
        };
    }

    return {
        minLength: min ?? undefined,
        maxLength: field.maxLength ?? len ?? max ?? undefined,
    };
}

function getDisabledDate(field, form, name) {
    const rules = field.validations ?? field.validation ?? field.rules ?? [];
    const predicates = [];
    for (const r of rules) {
        const type = typeof r === 'string' ? r : r?.type;
        if (type === 'noPastDate') {
            predicates.push((current) => {
                if (!current) return false;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return getComparableDateTime(current) < today.getTime();
            });
        }
        if (type === 'noFutureDate') {
            predicates.push((current) => {
                if (!current) return false;
                const endOfDay = new Date();
                endOfDay.setHours(23, 59, 59, 999);
                return getComparableDateTime(current) > endOfDay.getTime();
            });
        }
        if (type === 'minAge') {
            const years = Number(r?.value ?? 18);
            predicates.push((current) => {
                if (!current) return false;
                const cutoff = new Date();
                cutoff.setFullYear(cutoff.getFullYear() - years);
                cutoff.setHours(23, 59, 59, 999);
                return getComparableDateTime(current) > cutoff.getTime();
            });
        }
        if (type === 'dateAfterField' || type === 'dateBeforeField') {
            const compareField = r?.value ?? r?.compareField ?? r?.field;
            if (!compareField) continue;
            predicates.push((current) => {
                if (!current) return false;
                const compareValue = form.getFieldValue(getSiblingFieldName(name, compareField));
                if (empty(compareValue)) return false;
                const currentTime = getComparableDateDay(current);
                const compareTime = getComparableDateDay(compareValue);
                if (currentTime === null || compareTime === null) return false;
                return type === 'dateAfterField' ? currentTime < compareTime : currentTime > compareTime;
            });
        }
    }
    return predicates.length ? (current) => predicates.some((predicate) => predicate(current)) : undefined;
}

function getDefaultPickerValue(field) {
    const rules = field.validations ?? field.validation ?? field.rules ?? [];
    for (const r of rules) {
        const type = typeof r === 'string' ? r : r?.type;
        if (type === 'minAge') {
            const years = Number(r?.value ?? 18);
            return dayjs().subtract(years, 'year');
        }
        if (type === 'noFutureDate') return dayjs();
        if (type === 'noPastDate') return dayjs();
    }
    return undefined;
}

function cleanValue(value, formatter) {
    if (!formatter) return value;
    if (formatter === 'phone' || formatter === 'digits') return value.replace(/\D/g, '').slice(0, 10);
    if (formatter === 'email' || formatter === 'noSpaces') return value.replace(/\s/g, '');
    if (formatter === 'name') return value.replace(/\s+/g, ' ').trimStart().replace(/\b\w/g, (char) => char.toUpperCase());
    return value;
}

function normalizeModule(moduleName = '') {
    const normalized = String(moduleName).toLowerCase();
    if (normalized === 'job') return 'jobs';
    if (normalized === 'candidate') return 'candidates';
    if (normalized === 'submissions') return 'submission';
    return normalized;
}

const SEARCH_SOURCE_ONLY_FIELDS = new Set([
    'selectedSource',
    'source',
    'sourceType',
    'scope',
]);

function hasSearchCriterionValue(value) {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'number') return Number.isFinite(value);
    if (typeof value === 'boolean') return value;
    if (dayjs.isDayjs(value)) return value.isValid();
    if (Array.isArray(value)) return value.some(hasSearchCriterionValue);
    if (typeof value === 'object') {
        if ('value' in value || 'label' in value || 'name' in value) {
            return hasSearchCriterionValue(value.value ?? value.label ?? value.name);
        }
        return Object.entries(value).some(([key, nestedValue]) => (
            !SEARCH_SOURCE_ONLY_FIELDS.has(key) && hasSearchCriterionValue(nestedValue)
        ));
    }

    return false;
}

function hasSearchCriteria(values = {}) {
    return Object.entries(values ?? {}).some(([key, value]) => (
        !SEARCH_SOURCE_ONLY_FIELDS.has(key) && hasSearchCriterionValue(value)
    ));
}

// buildDynamicFormData — builds the multipart FormData payload to send to the
// Auth gateway.
//
//   • File fields are appended as separate FormData parts. The part name is the
//     field's `fileKey` if configured, else the first segment of its path
//     (e.g. "passport.passportUploadName" → "passport"). Files nested inside an
//     addRow group array are collected from every row.
//   • All other fields are turned into the JSON "json" part by the shared,
//     config-driven payload engine (buildPayload). There is ZERO field-name or
//     module-specific logic here — payloadKey / dataType / payloadMode / template
//     are all read from the admin form-group config.
//
// Dynamic by design: any new field added in the admin panel is automatically
// included — no code change required. New modules work the same way.
function buildDynamicFormData(values, groups, extraPayload = {}, module = '') {
    const formData = new FormData();

    // New files → multipart parts, via the SHARED engine helper. On CREATE we
    // attach only the flat (non-repeatable) file fields — resume, photo, jobs
    // "file", passport, visa. Repeatable (addRow) document files are uploaded in
    // a follow-up update (see submit) because the create handler mishandles them;
    // the indexed update path is the reliable one. The document METADATA still
    // rides along in the JSON payload here, so the rows are created on insert and
    // the follow-up update just attaches each file to its row by index.
    collectFileParts(values, groups, { scope: 'flat', module }).forEach(({ formKey, file }) => {
        formData.append(formKey, file);
    });

    // Config-driven JSON payload (file fields are skipped by the engine).
    // extraPayload carries linkage/context keys that are NOT form fields but the
    // downstream create requires (e.g. submission needs jobId + applicantId). These
    // are merged first so a real form field of the same name still wins.
    const payload = { ...extraPayload, ...buildPayload(values, groups, { module }) };
    payload.schemaVersion = 1;

    formData.append('json', JSON.stringify(payload));
    return formData;
}

function FieldControl({ field, form, name, moduleName, value, checked, onChange, scopeValues = {}, onAiUpload, formDisabled }) {
    const type = field.type ?? 'text';
    const baseDisabled = !editable(field, moduleName);
    const [dynamicOptions, setDynamicOptions] = useState(null);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [locationSearch, setLocationSearch] = useState('');
    const [lookupSearch, setLookupSearch] = useState('');
    const [masterSearch, setMasterSearch] = useState('');
    const [viewer, setViewer] = useState({ open: false, docs: [], index: 0 });
    const [inputError, setInputError] = useState(null);
    const rawLookupRef = useRef([]);
    // lookupFilterValueFrom resolves against the SAME addRow row first (so a
    // per-row cascade like employerId → that row's recruiter list works), then
    // falls back to the top-level path — which is what every pre-existing
    // top-level config used. Both are always watched (hooks can't be
    // conditional); the row value wins whenever it is non-empty.
    const insideListRow = Array.isArray(name) && name.length >= 3 && typeof name[1] === 'number';
    const topFilterPath = field.lookupFilterValueFrom ? namePathFromString(field.lookupFilterValueFrom) : [`__lookup_filter_${field.field}`];
    const rowFilterPath = (field.lookupFilterValueFrom && insideListRow)
        ? [...name.slice(0, 2), ...namePathFromString(field.lookupFilterValueFrom)]
        : ['__noop_row_filter__'];
    const topFilterValue = Form.useWatch(topFilterPath, form);
    const rowFilterValue = Form.useWatch(rowFilterPath, form);
    const formFilterValue = !empty(rowFilterValue) ? rowFilterValue : topFilterValue;
    // Watch sibling field for disabledByField — e.g. endDate disabled when currentlyPursuing is checked
    const disabledBySiblingPath = field.disabledByField
        ? (Array.isArray(name) && name.length > 1 ? [...name.slice(0, -1), field.disabledByField] : [field.disabledByField])
        : ['__noop_disabled_watch__'];
    const disabledByValue = Form.useWatch(disabledBySiblingPath, form);
    // disabledIf — conditional read-only, same operator set as showIf (eq/neq/
    // truthy/falsy/in/notIn), e.g. lock the Employer to the configured default
    // when Contract Type is W2. The row value wins when the condition field
    // lives in this row; otherwise the top-level field drives it.
    const disabledIfTopPath = field.disabledIf?.field ? namePathFromString(field.disabledIf.field) : ['__noop_disabled_if__'];
    const disabledIfRowPath = (field.disabledIf?.field && insideListRow)
        ? [...name.slice(0, 2), ...namePathFromString(field.disabledIf.field)]
        : ['__noop_disabled_if_row__'];
    const disabledIfTop = Form.useWatch(disabledIfTopPath, form);
    const disabledIfRow = Form.useWatch(disabledIfRowPath, form);
    const conditionallyDisabled = field.disabledIf?.field
        ? labelConditionMatches(field.disabledIf, !empty(disabledIfRow) ? disabledIfRow : disabledIfTop)
        : false;
    const disabled = baseDisabled || Boolean(field.disabled) || conditionallyDisabled || (field.disabledByField ? truthy(disabledByValue) : false) || Boolean(formDisabled);
    // scopeValues only wins when it actually carries a value — a key present
    // but undefined/empty must fall back to the field's own live form value,
    // or the lookup filter is permanently starved and never resolves options.
    const scopeFilterValue = field.lookupFilterValueFrom
        ? scopeValues[field.lookupFilterValueFrom]
        : undefined;
    const lookupFilterValue = !empty(scopeFilterValue) ? scopeFilterValue : formFilterValue;

    // Fetch options dynamically when dataSource is master / module / lookup.
    // Static and undefined sources skip the API call and use field.options directly.
    useEffect(() => {
        if (type === 'location') {
            let cancelled = false;
            setOptionsLoading(true);

            async function fetchLocations() {
                try {
                    const params = new URLSearchParams({
                        search: locationSearch,
                        limit: String(field.limit ?? 50),
                        offset: '0',
                    });
                    const json = await fetchJsonWithAuth(AUTH_URL, `/admin/location-dropdown-values?${params}`);
                    const raw = json?.data ?? json ?? [];
                    if (!cancelled) setDynamicOptions(normalizeOptions(Array.isArray(raw) ? raw : []));
                } catch (err) {
                    console.error(`[AddFormV1] Failed to fetch locations for "${field.field}":`, err);
                    if (!cancelled) setDynamicOptions([]);
                } finally {
                    if (!cancelled) setOptionsLoading(false);
                }
            }

            fetchLocations();
            return () => { cancelled = true; };
        }
        const ds = field.dataSource;
        if (!['select', 'radio', 'checkbox'].includes(type)) return undefined;
        if (!ds || ds === 'static') return undefined;
        if (ds === 'lookup' && field.lookupFilterField && empty(lookupFilterValue)) {
            setDynamicOptions([]);
            setOptionsLoading(false);
            return undefined;
        }

        let cancelled = false;
        setOptionsLoading(true);

        async function fetchOptions() {
            try {
                let raw = [];
                if (ds === 'master' || ds === 'module') {
                    raw = await getDropdownValues(
                        moduleName,
                        field.field,
                        ds === 'master' ? masterSearch : '',
                        field.limit ?? 50,
                        0,
                        {
                            dataSource: ds,
                            masterName: field.masterName,
                            groupName: field.groupName,
                        },
                    );
                } else if (ds === 'lookup') {
                    const params = new URLSearchParams({
                        collection: field.lookupCollection ?? '',
                        displayField: field.displayField ?? '',
                        valueField: field.valueField ?? '_id',
                    });
                    if (field.displayField2) params.set('displayField2', field.displayField2);
                    if (lookupSearch) params.set('search', lookupSearch);
                    if (field.lookupFilterField && !empty(lookupFilterValue)) {
                        params.set('filterField', field.lookupFilterField);
                        params.set('filterValue', String(lookupFilterValue));
                    }
                    if (field.extraFields?.length) params.set('extraFields', field.extraFields.join(','));
                    const json = await fetchJsonWithAuth(AUTH_URL, `/admin/lookup-dropdown-values?${params}`);
                    raw = json?.data ?? json ?? [];
                    // e.g. only list a recruiter once verified (extraData["recruiters.isValid"] === true)
                    if (field.optionsRequireExtraField) {
                        raw = raw.filter((item) => item?.extraData?.[field.optionsRequireExtraField] === true);
                    }
                } else if (ds === 'group') {
                    const gn = (field.groupName ?? '').toLowerCase().trim();
                    if (gn === 'users' || gn === 'user') {
                        const json = await fetchJsonWithAuth(AUTH_URL, '/get-all-users?limit=500&offset=0');
                        const users = json?.data?.users ?? json?.users ?? json?.data ?? [];
                        raw = Array.isArray(users) ? users.map((u) => ({
                            label: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.userName || u.email || String(u.userId),
                            value: u.userId ?? u.id,
                        })) : [];
                    }
                }
                if (!cancelled) {
                    rawLookupRef.current = Array.isArray(raw) ? raw : [];
                    setDynamicOptions(normalizeOptions(rawLookupRef.current));
                }
            } catch (err) {
                console.error(`[AddFormV1] Failed to fetch options for "${field.field}":`, err);
                if (!cancelled) setDynamicOptions([]);
            } finally {
                if (!cancelled) setOptionsLoading(false);
            }
        }

        fetchOptions();
        return () => { cancelled = true; };
    }, [type, field.dataSource, field.field, field.limit, field.lookupCollection, field.displayField, field.displayField2, field.valueField, field.lookupFilterField, lookupFilterValue, field.groupName, field.masterName, locationSearch, lookupSearch, masterSearch, moduleName, field.extraFields, field.optionsRequireExtraField]);

    const staticOptions = normalizeOptions(field.options ?? field.values ?? []);
    const options = dynamicOptions ?? staticOptions;

    // Select multiplicity is config-driven: multiSelect flag → explicit mode →
    // field.multiple → array dataType. No field-name list. "tags" is opt-in via
    // an explicit field.mode === "tags".
    const selectMode = field.multiSelect
        ? 'multiple'
        : (field.mode ?? (truthy(field.multiple) ? 'multiple' : undefined));
    const inferredSelectMode = selectMode ?? (effectiveDataType(field) === 'array' ? 'multiple' : undefined);

    const isSelectLike = ['select', 'location'].includes(type);
    const placeholder = field.hidePlaceholder
        ? undefined
        : (field.placeholder ?? `${isSelectLike ? 'Select' : 'Enter'} ${String(field.label ?? '').toLowerCase()}`);
    const inputLimits = getInputLimits(field);
    // Sets the value in the form store first, then re-validates.
    // Required because FieldControl is a custom wrapper — Form.Item's injected
    // onChange never reaches the inner Ant Design components, so the store
    // would otherwise hold stale/undefined values when validation runs.
    const updateAndValidate = (nextValue) => {
        onChange?.(nextValue);

        setTimeout(() => {
            form.validateFields([name]).catch(() => { });
        }, 0);
    };

    // Build validator config once (null when not set)
    const validatorCfg = field.validator?.type
        ? { ...field.validator, title: field.validator.title || field.label || 'Field' }
        : null;

    const inputProps = {
        ...inputLimits,
        disabled,
        placeholder,
        prefix: field.icon ? null : fieldIcons[field.field],
        value: value ?? '',
        onChange: (event) => {
            let nextValue = field.formatter
                ? cleanValue(event.target.value, field.formatter)
                : event.target.value;

            if (validatorCfg) {
                const { cleaned, error } = applyInputValidator(nextValue, validatorCfg, 'change');
                setInputError(error);
                nextValue = cleaned;
            }

            updateAndValidate(nextValue);
        },
        ...(validatorCfg && {
            onBlur: (e) => {
                const { cleaned, error } = applyInputValidator(e.target.value, validatorCfg, 'blur');
                setInputError(error);
                if (cleaned !== e.target.value) updateAndValidate(cleaned);
            },
            onKeyPress: (e) => {
                const pattern = getKeyPattern(validatorCfg.type);
                if (!pattern) return;
                if (e.which === 0 || e.keyCode === 8 || e.keyCode === 13) return;
                const char = String.fromCharCode(e.which ?? e.keyCode);
                if (!pattern.test(char)) {
                    e.preventDefault();
                    const hint = `${validatorCfg.title} — invalid character.`;
                    setInputError(hint);
                    setTimeout(() => setInputError((prev) => (prev === hint ? null : prev)), 1800);
                }
            },
            maxLength: validatorCfg.maxLength ?? inputLimits.maxLength,
        }),
    };

    if (type === 'tagify') {
        return (
            <Select
                value={value}
                allowClear
                disabled={disabled}
                mode="tags"
                placeholder={placeholder}
                onChange={updateAndValidate}
            />
        );
    }
    if (type === 'location') {
        return (
            <Select
                value={value}
                allowClear
                showSearch
                filterOption={false}
                disabled={disabled}
                loading={optionsLoading}
                options={options}
                placeholder={placeholder}
                notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
                onSearch={setLocationSearch}
                onFocus={() => setLocationSearch((current) => current)}
                onChange={updateAndValidate}
            />
        );
    }
    if (type === 'select') {
        const remoteLookup = field.dataSource === 'lookup';
        const remoteMaster = field.dataSource === 'master';
        const remoteSearch = remoteLookup || remoteMaster;
        const localGroupSearch = field.dataSource === 'group';
        const keepLabel = usesLabeledSelectValue(field);
        const controlValue = toSelectControlValue(field, value, options);
        const handleSelectChange = (val) => {
            updateAndValidate(val);
            if (field.autofillFrom?.length) {
                const selectedValue = val && !Array.isArray(val) && typeof val === 'object' ? val.value : val;
                const isInsideList = Array.isArray(name) && name.length >= 3 && typeof name[1] === 'number';
                if (val == null) {
                    // Cleared — wipe all autofill targets
                    field.autofillFrom.forEach(({ targetField: tf }) => {
                        const tp = isInsideList
                            ? [...name.slice(0, 2), ...namePathFromString(tf)]
                            : namePathFromString(tf);
                        form.setFieldValue(tp, null);
                    });
                } else if (rawLookupRef.current.length > 0) {
                    const vField = field.valueField ?? '_id';
                    const rawItem = rawLookupRef.current.find(item =>
                        getNestedProp(item, vField) === selectedValue || item._id === selectedValue || item.id === selectedValue || item.value === selectedValue
                    );
                    if (rawItem) {
                        field.autofillFrom.forEach(({ optionKey, targetField: tf }) => {
                            const v = getNestedProp(rawItem, optionKey) ?? rawItem.extraData?.[optionKey] ?? null;
                            const tp = isInsideList
                                ? [...name.slice(0, 2), ...namePathFromString(tf)]
                                : namePathFromString(tf);
                            form.setFieldValue(tp, v);
                        });
                    }
                }
            }
        };
        return (
            <Select
                value={controlValue}
                labelInValue={keepLabel || undefined}
                allowClear
                showSearch={remoteSearch || localGroupSearch || undefined}
                optionFilterProp="label"
                filterOption={remoteSearch ? false : undefined}
                disabled={disabled}
                loading={optionsLoading}
                mode={inferredSelectMode}
                options={options}
                placeholder={placeholder}
                notFoundContent={optionsLoading ? <Spin size="small" /> : undefined}
                onSearch={remoteLookup ? setLookupSearch : remoteMaster ? setMasterSearch : undefined}
                onChange={handleSelectChange}
            />
        );
    }
    if (type === 'radio') {
        return (
            <Radio.Group
                value={value}
                disabled={disabled}
                options={options}
                onChange={(e) => updateAndValidate(e.target.value)}
            />
        );
    }
    if (type === 'checkbox') {
        if (options.length > 0) {
            return (
                <Checkbox.Group
                    value={value}
                    disabled={disabled}
                    options={options}
                    onChange={updateAndValidate}
                />
            );
        }
        return (
            <Checkbox
                checked={Boolean(checked ?? value)}
                disabled={disabled}
                onChange={(e) => {
                    const next = e.target.checked;
                    updateAndValidate(next);
                    if (field.linkedClearField && next) {
                        const clearPath = Array.isArray(name) && name.length > 1
                            ? [...name.slice(0, -1), field.linkedClearField]
                            : [field.linkedClearField];
                        form.setFieldValue(clearPath, null);
                    }
                }}
            >
                {field.checkboxLabel ?? field.label}
            </Checkbox>
        );
    }
    if (type === 'text-editor') {
        return (
            <>
                <TipTapEditor
                    value={value ?? form.getFieldValue(name) ?? ''}
                    disabled={disabled}
                    placeholder={placeholder}
                    onChange={(html) => {
                        if (validatorCfg) {
                            const { error } = applyInputValidator(html, validatorCfg, 'change');
                            setInputError(error);
                        }
                        updateAndValidate(html);
                    }}
                />
                {inputError && <div className="v1-input-validator-hint">{inputError}</div>}
            </>
        );
    }
    if (type === 'textarea') {
        return (
            <>
                <TextArea {...inputProps} rows={field.rows ?? 3} className="v1-scroll-textarea" />
                {inputError && <div className="v1-input-validator-hint">{inputError}</div>}
            </>
        );
    }
    if (type === 'date') {
        return (
            <DatePicker
                disabled={disabled}
                placeholder={placeholder}
                style={{ width: '100%' }}
                suffixIcon={<CalendarOutlined />}
                value={value ?? null}
                disabledDate={getDisabledDate(field, form, name)}
                defaultPickerValue={getDefaultPickerValue(field)}
                onChange={(date) => updateAndValidate(date)}
            />
        );
    }
    if (type === 'time') return <TimePicker disabled={disabled} placeholder={placeholder} style={{ width: '100%' }} value={value ?? null} onChange={(time) => updateAndValidate(time)} />;
    if (type === 'file') {
        const fileRules = field.validations ?? field.validation ?? field.rules ?? [];
        const fileTypeRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'fileType');
        const fileSizeRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'fileSize');
        const maxFilesRule = fileRules.find((r) => (typeof r === 'string' ? r : r?.type) === 'maxFiles');

        // Admin "Allowed Content" category (field.accept) takes priority over the
        // fine-grained fileType rule; 'any'/unset falls through to the rule.
        const acceptCategory = uploadAcceptCategory(field);
        const acceptTypes = acceptCategory
            ? acceptCategory.exts.map((e) => `.${e}`).join(',')
            : (fileTypeRule?.value
                ? fileTypeRule.value.split(',').map((e) => `.${e.trim().replace(/^\./, '')}`).join(',')
                : undefined);
        const maxSizeMB = fileSizeRule ? Number(fileSizeRule?.value ?? 5) : undefined;
        // maxFiles validation rule takes top priority; otherwise use field.multiple/maxCount
        // (set via admin panel toggle), falling back to single-file mode.
        const maxFiles = maxFilesRule
            ? Number(maxFilesRule?.value ?? 1)
            : (truthy(field.multiple) ? (field.maxCount ?? 5) : 1);

        const fileList = Array.isArray(value) ? value : [];
        const canAddMore = fileList.length < maxFiles;

        const formatFileSize = (bytes) => {
            if (!bytes) return '';
            if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
            return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
        };

        const getFileIcon = (fileName) => {
            const ext = (fileName ?? '').split('.').pop()?.toLowerCase();
            if (ext === 'pdf') return <FilePdfOutlined className="v1-file-type-icon v1-file-icon-pdf" />;
            if (['doc', 'docx'].includes(ext)) return <FileWordOutlined className="v1-file-type-icon v1-file-icon-word" />;
            if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileExcelOutlined className="v1-file-type-icon v1-file-icon-excel" />;
            if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return <FileImageOutlined className="v1-file-type-icon v1-file-icon-image" />;
            return <FileOutlined className="v1-file-type-icon v1-file-icon-default" />;
        };

        const handleFileDelete = (index) => {
            updateAndValidate(fileList.filter((_, i) => i !== index));
        };

        const handleFileView = (file, fileIdx) => {
            const docs = fileList.map((f) => {
                let url = f.url ?? f.thumbUrl ?? '';
                if (!url && f.originFileObj instanceof File) url = URL.createObjectURL(f.originFileObj);
                return { url, name: f.name ?? 'file' };
            }).filter(d => d.url);
            setViewer({ open: true, docs, index: fileIdx ?? 0 });
        };

        const isMulti = maxFiles > 1;
        const remaining = maxFiles - fileList.length;

        const typeHint = acceptCategory
            ? acceptCategory.hint
            : (acceptTypes
                ? acceptTypes.split(',').map((t) => t.replace('.', '').toUpperCase()).join(', ')
                : null);
        const sizeHint = maxSizeMB ? `up to ${maxSizeMB} MB` : null;
        const baseHint = [typeHint, sizeHint].filter(Boolean).join(' ');
        const dropHint = isMulti && fileList.length > 0
            ? [baseHint, `${remaining} of ${maxFiles} remaining`].filter(Boolean).join(' • ')
            : baseHint;
        const dropTitle = fileList.length > 0
            ? `Add ${isMulti ? 'more files' : 'file'}`
            : (field.buttonLabel ?? `Upload ${field.label ?? 'File'}`);

        const beforeUploadHandler = (file) => {
            if (fileList.length >= maxFiles) return Upload.LIST_IGNORE;
            if (acceptTypes) {
                const ext = (file.name.split('.').pop() ?? '').toLowerCase();
                const allowed = acceptTypes.split(',').map((t) => t.replace(/^\./, '').toLowerCase());
                if (!allowed.includes(ext)) {
                    message.error(acceptCategory
                        ? acceptCategory.error
                        : (fileTypeRule?.message ?? `Only ${fileTypeRule.value} files are allowed`));
                    return Upload.LIST_IGNORE;
                }
            }
            if (maxSizeMB !== undefined && file.size / 1024 / 1024 > maxSizeMB) {
                message.error(fileSizeRule?.message ?? `File must be smaller than ${maxSizeMB} MB`);
                return Upload.LIST_IGNORE;
            }
            return false;
        };

        // antd calls onChange once per file even in multi-select, each time with the
        // full accumulated newList. Slicing to maxFiles guards against racing additions.
        const handleUploadChange = ({ fileList: newList }) => {
            const sliced = newList.slice(0, maxFiles);
            updateAndValidate(sliced);
            // An "upload"-trigger AI action (e.g. auto-parse a resume) fires once
            // per newly-added file, using the raw browser File object.
            if (onAiUpload && sliced.length > fileList.length) {
                const added = sliced[sliced.length - 1];
                if (added?.originFileObj) onAiUpload(added.originFileObj);
            }
        };

        const multiUploadProps = {
            accept: acceptTypes,
            fileList,
            showUploadList: false,
            multiple: true,
            maxCount: maxFiles,
            beforeUpload: beforeUploadHandler,
            onChange: handleUploadChange,
            disabled,
        };

        const fileRows = fileList.map((file, index) => (
            <div key={file.uid ?? index} className="v1-file-item">
                <div className="v1-file-item-icon">{getFileIcon(file.name)}</div>
                <div className="v1-file-item-info">
                    <span className="v1-file-item-name">{file.name}</span>
                    <span className="v1-file-item-meta">
                        {file.existing
                            ? 'Previously uploaded'
                            : `${formatFileSize(file.size)}${file.size ? ' • ' : ''}Uploaded just now`}
                    </span>
                </div>
                <Space size={6} className="v1-file-item-actions">
                    <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleFileView(file, index)}
                        className="v1-file-view-btn"
                    >
                        View
                    </Button>
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleFileDelete(index)}
                        className="v1-file-delete-btn"
                    />
                </Space>
            </div>
        ));

        const viewerEl = (
            <DocumentViewer
                documents={viewer.docs}
                open={viewer.open}
                onClose={() => setViewer(v => ({ ...v, open: false }))}
                initialIndex={viewer.index}
            />
        );

        if (isMulti) {
            const multiTitle = fileList.length > 0
                ? `Upload more ${field.label ?? 'files'}`
                : (field.buttonLabel ?? `Upload ${field.label ?? 'File'}`);

            return (
                <>
                    <div className="v1-file-upload-wrapper">
                        {/* Centered dropzone — always at top in multiple mode */}
                        {canAddMore && (
                            <Upload {...multiUploadProps} style={{ width: '100%', display: 'block' }}>
                                <div className={`v1-upload-dropzone v1-upload-dropzone-center${disabled ? ' v1-upload-dropzone-disabled' : ''}`}>
                                    <div className="v1-upload-dropzone-title-row">
                                        <CloudUploadOutlined className="v1-upload-cloud-icon v1-upload-cloud-icon-sm" />
                                        <span className="v1-upload-dropzone-title v1-upload-dropzone-title-blue">{multiTitle}</span>
                                    </div>
                                    <span className="v1-upload-dropzone-sub">Drag & drop or click to upload</span>
                                    {baseHint && <span className="v1-upload-dropzone-sub">{baseHint}</span>}
                                </div>
                            </Upload>
                        )}

                        {/* Uploaded file rows — 2-column grid */}
                        {fileList.length > 0 && (
                            <div className="v1-file-grid">
                                {fileRows}
                            </div>
                        )}

                        {/* "+ Add more files" text link at the bottom */}
                        {canAddMore && fileList.length > 0 && (
                            <Upload {...multiUploadProps} style={{ display: 'inline-block' }}>
                                <Button type="link" size="small" icon={<PlusOutlined />} className="v1-add-more-files-btn">
                                    Add more files
                                </Button>
                            </Upload>
                        )}
                    </div>
                    {viewerEl}
                </>
            );
        }

        return (
            <>
                <div className="v1-file-upload-wrapper">
                    {/* Single mode: compact file row at field height */}
                    {fileList.length > 0 && <div className="v1-file-rows-single">{fileRows}</div>}

                    {canAddMore && (
                        <Upload
                            accept={acceptTypes}
                            fileList={fileList}
                            showUploadList={false}
                            multiple={false}
                            maxCount={1}
                            beforeUpload={beforeUploadHandler}
                            onChange={handleUploadChange}
                            disabled={disabled}
                            style={{ width: '100%', display: 'block' }}
                        >
                            <div className={`v1-upload-dropzone v1-upload-dropzone-single${disabled ? ' v1-upload-dropzone-disabled' : ''}`}>
                                <CloudUploadOutlined className="v1-upload-cloud-icon" />
                                <span className="v1-upload-dropzone-title">{dropTitle}</span>
                                {dropHint && <span className="v1-upload-dropzone-sub">— {dropHint}</span>}
                            </div>
                        </Upload>
                    )}
                </div>
                {viewerEl}
            </>
        );
    }

    return (
        <>
            <Input
                {...inputProps}
                type={type === 'number' ? 'number' : type}
                // step="any" so the browser's own number input accepts a
                // fractional value on a field the admin marked decimal.
                {...(type === 'number' && truthy(field.decimal) ? { step: 'any' } : {})}
                // Admin-configured unit shown inside the input (e.g. "%").
                {...(field.suffix ? { suffix: field.suffix } : {})}
            />
            {inputError && <div className="v1-input-validator-hint">{inputError}</div>}
        </>
    );
}

function SectionTitle({ icon, title }) {
    return (
        <Space size={10} align="center">
            <div className="v1-section-icon-box">{icon}</div>
            <Text strong className="v1-section-label">{title}</Text>
        </Space>
    );
}

function FieldLabel({ field, label, locked }) {
    if (!label) return label;
    const showInfo = truthy(field?.infoEnabled) && !empty(field?.infoText);
    if (!showInfo && !locked) return label;
    return (
        <span className="v1-field-label-with-info">
            <span>{label}</span>
            {showInfo && (
                <Tooltip title={field.infoText}>
                    <InfoCircleOutlined className="v1-field-info-icon" />
                </Tooltip>
            )}
            {locked && (
                <Tooltip title="You don't have permission to edit this field">
                    <LockOutlined className="v1-field-info-icon" />
                </Tooltip>
            )}
        </span>
    );
}

function GroupInfoMessage({ group }) {
    if (!truthy(group?.infoEnabled) || empty(group?.infoText)) return null;
    return <Text type="secondary" className="v1-group-info-message">{group.infoText}</Text>;
}

function GroupToggleControl({ group }) {
    if (!truthy(group?.toggleEnabled) || empty(group?.toggleField)) return null;
    return (
        <Space size={6} align="center" onClick={(event) => event.stopPropagation()}>
            <Text type="secondary" className="v1-combine-toggle-label">{group.toggleLabel || 'Toggle'}</Text>
            <Form.Item name={group.toggleField} valuePropName="checked" initialValue={Boolean(group.toggleValue)} noStyle>
                <Switch size="small" />
            </Form.Item>
        </Space>
    );
}

export default function AddFormV1({
    moduleName,
    action,
    clientId,
    region,
    formGroups: providedFormGroups,
    loading: providedLoading = false,
    error: providedError = '',
    breadcrumbItems = [],
    // formType — controls the mode of operation:
    //   'add'    → Add Form (default, existing behavior)
    //   'search' → Search Form (lighter validation, Reset button, no auto-create)
    formType = 'add',
    submitText: propSubmitText,
    cancelPath = '/candidates',
    onSubmit,
    // initialRecord — prefill flat form fields from a plain record object.
    // Matched by field.field / payloadKey / dot-path; best-effort, never required.
    initialRecord = null,
    // prefillFromUrl — when set (quickSubmit flow), AddFormV1 reads the route to
    // decide the prefill source and the job/candidate linkage entirely on its own:
    //   • isFromSubmission=true + submissionId → read the submissions collection
    //   • otherwise candidateId                → read the candidates collection
    // Both resolve through getFormGroups(id) (field.value + group.rows embedded) —
    // no separate detail-view API. The linkage the submission create requires
    // (applicantId + jobId) is derived from the same URL params.
    prefillFromUrl = false,
    // extraPayload — context/linkage keys merged into the create payload that are
    // NOT form fields (e.g. submission needs { jobId, applicantId }). Kept out of
    // the form so the config stays generic. Ignored when prefillFromUrl derives
    // its own linkage from the route.
    extraPayload = null,
    // presetClientId — caller already knows the client (e.g. Quick Submit knows
    // it from the selected job) so the client-scope gate should resolve silently
    // instead of showing the manual "Select Client" picker.
    presetClientId = null,
    // ListView props for search mode — passed through from parent when search results
    // should be displayed below the search form.
    listViewProps = {},
    // selectedScope — optional scope parameter for filtering search results
    selectedScope = '',
    // searchActionContext — page-level context (e.g. { jobId }) merged into every
    // search-result row action's popupState, so a popup opened from here (like
    // Source Candidates' "Shortlisted"/"Tag to Jobs") already knows what the
    // caller knows instead of asking the user to re-select it. Mirrors the
    // action.popupState convention ListView already spreads into the popup's
    // navigation state — see ListView.jsx's handleActionClick.
    searchActionContext = null,
}) {
    const isSearch = formType === 'search';
    const submitText = propSubmitText ?? (isSearch ? 'Search' : 'Create');
    const navigate = useNavigate();
    const routeParams = useParams();
    const [searchParams] = useSearchParams();
    const [form] = Form.useForm();
    const searchFormValues = Form.useWatch([], form);
    const [searchLoading, setSearchLoading] = useState(false);
    const [collapsed, setCollapsed] = useState({});
    const [apiFormGroups, setApiFormGroups] = useState([]);
    const [apiLoading, setApiLoading] = useState(Boolean(moduleName));
    const [apiError, setApiError] = useState('');
    const [searchFormLabel, setSearchFormLabel] = useState('');
    const [submitLoading, setSubmitLoading] = useState(false);
    // Search results state — used when formType === 'search' to store and display results
    const [searchResults, setSearchResults] = useState({
        searched: false,
        rows: [],
        total: 0,
        fields: [],
        actions: [],
        actionRules: [],
        loading: false,
    });
    const [searchPagination, setSearchPagination] = useState({ current: 1, pageSize: 10 });
    const [lastSearchValues, setLastSearchValues] = useState({});
    const [selectedSource, setSelectedSource] = useState('internal');
    const [tabs, setTabs] = useState([]);
    const [sourceActionState, setSourceActionState] = useState({});
    const [clientOptions, setClientOptions] = useState([]);
    const [scopeClientId, setScopeClientId] = useState(clientId ?? '');
    const [scopeRegion, setScopeRegion] = useState(region ?? '');
    // URL-driven prefill state — populated when prefillFromUrl is set. Holds the
    // source record's form groups (candidate OR submission) with values embedded.
    const [prefillGroups, setPrefillGroups] = useState(null);
    const [prefillLoading, setPrefillLoading] = useState(false);
    // Role Configure → Form permissions for the CURRENT user's role, derived
    // server-side from this module's Form Groups config (see
    // GetRoleFormPermissions). null until loaded / when unavailable, in which
    // case normalizeGroups' rolePerms checks fail open — no behavior change
    // for a role/module that hasn't been configured.
    const [rolePerms, setRolePerms] = useState(null);
    // Default Fields (Form Groups → Default Fields) — always-shown + required
    // field keys for this module. Empty set until loaded / when unconfigured.
    const [defaultFieldSet, setDefaultFieldSet] = useState(() => new Set());
    const formGroups = moduleName ? apiFormGroups : (providedFormGroups ?? emptyGroups);
    const loading = moduleName ? apiLoading : providedLoading;
    const error = moduleName ? apiError : providedError;
    const effectiveClientId = clientId || presetClientId || scopeClientId;
    const effectiveRegion = region || scopeRegion;

    useEffect(() => {
        let cancelled = false;
        queueMicrotask(() => {
            if (cancelled) return;
            if (!moduleName) { setRolePerms(null); return; }
            // Same client/region scope the form's own structure is fetched
            // with (getFormGroups below) — a client-scoped Form Groups
            // override must gate the SAME scoped structure, not the
            // tenant-wide default.
            getRoleFormPermissions({ roleId: localStorage.getItem('roleId') }, moduleName, { clientId: effectiveClientId, region: effectiveRegion })
                .then((data) => { if (!cancelled) setRolePerms(Array.isArray(data) && data.length ? data : null); })
                .catch(() => { if (!cancelled) setRolePerms(null); });
        });
        return () => { cancelled = true; };
    }, [moduleName, effectiveClientId, effectiveRegion]);

    useEffect(() => {
        let cancelled = false;
        queueMicrotask(() => {
            if (cancelled) return;
            if (!moduleName) { setDefaultFieldSet(new Set()); return; }
            getModuleDefaultFields(moduleName)
                .then((keys) => { if (!cancelled) setDefaultFieldSet(new Set(Array.isArray(keys) ? keys : [])); })
                .catch(() => { if (!cancelled) setDefaultFieldSet(new Set()); });
        });
        return () => { cancelled = true; };
    }, [moduleName]);

    const groups = useMemo(() => normalizeGroups(formGroups, moduleName, rolePerms, defaultFieldSet), [formGroups, moduleName, rolePerms, defaultFieldSet]);
    // group.linkGroup — addRow groups sharing the same value stay row-synced
    // (see form/linkedAddRowGroups.js). Empty for every group with no
    // linkGroup, i.e. every group that predates this feature.
    const linkedGroupSets = useMemo(() => computeLinkedSets(groups), [groups]);
    // Registry of each addRow group's live Form.List {add, remove}, written on
    // every render from inside the Form.List's own render-prop (synchronous
    // within the same commit, so it's fully populated before a user can click
    // anything). A linked group's "Add Row" / row-delete calls every member's
    // add()/remove() through this registry; unlinked groups just call their own.
    const listControlsRef = useRef({});
    // When an ADDITIONAL submit button (group.formActions, e.g. "Create and
    // Source Candidate") is clicked, it stashes its own afterSubmit here so the
    // shared submit() navigates per that button instead of the group default.
    // Reset after every submit and by the plain Create button.
    const pendingAfterSubmitRef = useRef(null);
    const formActions = useMemo(() => groups.flatMap((g) => g.formActions ?? []), [groups]);
    const aiActions = useAiActions({ form, groups, module: moduleName });
    const [viewMode, setViewMode] = useState("list");
    const searchDisabled = isSearch && !hasSearchCriteria(searchFormValues);

    // Scan raw formGroups (before normalizeGroups strips the clientScope field) to
    // auto-detect whether this module needs a client gate and read its optional message.
    // The Admin field config is the only source that enables this gate.
    const { hasClientScope, clientGateMessage, clientGateResetOnChange } = useMemo(() => {
        const gateField = extractGroups(formGroups).flatMap(g => g.fields ?? []).find(f => truthy(f.clientScope));
        return gateField
            ? {
                hasClientScope: true,
                clientGateMessage: gateField.clientGateMessage || '',
                clientGateResetOnChange: truthy(gateField.clientGateResetOnChange),
            }
            : { hasClientScope: false, clientGateMessage: '', clientGateResetOnChange: false };
    }, [formGroups]);

    const handleClientGateChange = (nextClientId) => {
        if (clientGateResetOnChange) {
            form.resetFields();
            // resetFields restores configured initial/default values. Explicitly
            // clear every root key as well so changing the Client Gate truly
            // removes nested values, files and repeatable-group rows.
            const storedValues = form.getFieldsValue(true);
            form.setFieldsValue(Object.fromEntries(
                Object.keys(storedValues).map((fieldName) => [fieldName, undefined]),
            ));
            setSearchResults({ searched: false, rows: [], total: 0, fields: [], loading: false });
            setSearchPagination({ current: 1, pageSize: 10 });
        }
        setScopeClientId(nextClientId ?? '');
    };

    const waitingForClientScope = hasClientScope && !effectiveClientId;

    useEffect(() => {
        if (!hasClientScope || presetClientId) return;
        getAdminClients({ limit: 200 })
            .then(setClientOptions)
            .catch(() => setClientOptions([]));
    }, [hasClientScope, presetClientId]);

    useEffect(() => {
        if (!moduleName) return undefined;

        let cancelled = false;

        async function fetchFormGroups() {
            try {
                setApiLoading(true);
                setApiError('');
                const data = isSearch
                    ? await getCustomForms(moduleName, 'search', action)
                    : await getFormGroups({ module: moduleName, action, clientId: effectiveClientId, region: effectiveRegion });
                if (!cancelled) {
                    setApiFormGroups(extractGroups(isSearch ? data?.[0] : data));
                    if (isSearch) setSearchFormLabel(data?.[0]?.label || '');
                }
            } catch (err) {
                console.error(`Failed to fetch ${moduleName} form groups:`, err);
                if (!cancelled) setApiError(err?.message || 'Failed to fetch form groups');
            } finally {
                if (!cancelled) setApiLoading(false);
            }
        }

        fetchFormGroups();

        return () => {
            cancelled = true;
        };
    }, [moduleName, action, isSearch, effectiveClientId, effectiveRegion]);

    // ── URL-driven prefill source + linkage ──
    // Decode the route once. The submission flow arrives at
    //   /submission-create/:jobId?candidateId=…&isFromSubmission=…[&submissionId=…]
    //   • isFromSubmission=true + submissionId → prefill from the submissions record
    //   • otherwise candidateId                → prefill from the candidates record
    const urlPrefill = useMemo(() => {
        if (!prefillFromUrl) return null;

        const candidateId = searchParams.get('candidateId') || '';
        const submissionId = searchParams.get('submissionId') || '';
        const fromSubmission = String(searchParams.get('isFromSubmission')) === 'true';
        const jobId = (routeParams.jobId && routeParams.jobId !== '_')
            ? routeParams.jobId
            : (searchParams.get('jobId') || '');

        const source = (fromSubmission && submissionId)
            ? { module: 'submissions', id: submissionId }
            : (candidateId ? { module: 'candidates', id: candidateId } : null);

        // Linkage the submission create requires (NOT form fields).
        const linkage = {};
        if (candidateId) linkage.applicantId = candidateId;
        if (jobId) linkage.jobId = jobId;

        return { source, linkage: Object.keys(linkage).length ? linkage : null };
    }, [prefillFromUrl, searchParams, routeParams]);

    // ── Prefill fetch: load the source record's form groups with embedded values ──
    // The backend embeds field.value for scalar fields and group.rows for addRow
    // groups (workExperience, educationDetails, documents). Single source of truth
    // for BOTH the candidate and submission flows — no detail-view API call.
    useEffect(() => {
        const source = urlPrefill?.source;
        if (!source) return undefined;

        let cancelled = false;

        async function fetchPrefill() {
            try {
                setPrefillLoading(true);
                const data = await getFormGroups({ module: source.module, id: source.id });
                if (!cancelled) setPrefillGroups(extractGroups(data));
            } catch (err) {
                console.error('Failed to fetch prefill data:', err);
                if (!cancelled) setPrefillGroups([]);
            } finally {
                if (!cancelled) setPrefillLoading(false);
            }
        }

        fetchPrefill();

        return () => { cancelled = true; };
    }, [urlPrefill]);

    // addRow rows from the prefill source, keyed by group.payloadKey (e.g.
    // "workExperience"). Submission addRow groups that share the same payloadKey
    // receive these rows as their Form.List initialValue at mount. Works for both
    // sources because the DB array element keys are identical (companyName,
    // jobTitle, qualification, …).
    const addRowPrefillsByKey = useMemo(() => {
        if (!prefillGroups) return {};
        return Object.fromEntries(
            prefillGroups
                .filter(g => g.addRow && Array.isArray(g.rows) && g.rows.length > 0)
                .map(g => [g.payloadKey || g.name, g.rows]),
        );
    }, [prefillGroups]);

    // Flat record extracted from the prefill source's scalar fields — prefills
    // form fields whose field.field (or payloadKey) matches a source field.
    const prefillFlatRecord = useMemo(
        () => flatRecordFromGroups(prefillGroups),
        [prefillGroups],
    );

    // ── Cross-module context prefill (field.prefillFromModule) ──────────────
    // A field may prefill from a DIFFERENT module's record than the default
    // prefill source — e.g. submission.mspReqId ← the current JOB's mspreqId on
    // Quick Submit. The admin sets prefillFromModule (which module) +
    // prefillFrom (which key); the record id comes from the same URL linkage the
    // create payload already uses (jobId → jobs, applicantId → candidates).
    const contextPrefillModules = useMemo(() => {
        const wanted = new Set();
        groups.forEach((group) => (group.fields ?? []).forEach((field) => {
            if (field.prefillFromModule) wanted.add(normalizeModule(field.prefillFromModule));
        }));
        return [...wanted];
    }, [groups]);

    const [contextRecords, setContextRecords] = useState({});
    useEffect(() => {
        const linkage = urlPrefill?.linkage;
        if (!linkage || contextPrefillModules.length === 0) return undefined;
        let cancelled = false;
        const idForModule = (mod) => (
            mod === 'jobs' ? linkage.jobId
                : mod === 'candidates' ? linkage.applicantId
                    : undefined
        );
        contextPrefillModules.forEach(async (mod) => {
            const id = idForModule(mod);
            if (!id) return;
            try {
                const data = await getFormGroups({ module: mod, id });
                const record = flatRecordFromGroups(extractGroups(data));
                if (!cancelled && record) {
                    setContextRecords((prev) => ({ ...prev, [mod]: record }));
                }
            } catch (err) {
                console.error(`Context prefill fetch failed for ${mod}:`, err);
            }
        });
        return () => { cancelled = true; };
    }, [contextPrefillModules, urlPrefill]);

    // ── Prefill from initialRecord / prefillFlatRecord (best-effort) ──
    // Matches each non-file scalar field by field.field → payloadKey on the
    // source record and sets it via setFieldsValue. addRow groups are seeded via
    // Form.List initialValue (see getAddRowInitialValue below) — setFieldsValue
    // on a Form.List that mounted empty only keeps the last row.
    useEffect(() => {
        const sourceRecord = initialRecord ?? prefillFlatRecord;
        // Runs when EITHER the default source or a cross-module context record is
        // ready — a form with only prefillFromModule fields (no candidate source,
        // e.g. Quick Submit straight from a job) must still prefill those.
        if ((!sourceRecord && Object.keys(contextRecords).length === 0)
            || loading || groups.length === 0) return;
        const values = {};
        groups.forEach((group) => {
            if (group.toggleEnabled && group.toggleField && sourceRecord) {
                const stored = pickPrefillValue(sourceRecord, { field: group.toggleField });
                if (stored !== undefined && stored !== null) values[group.toggleField] = Boolean(stored);
            }
            if (group.addRow) return;
            (group.fields ?? []).forEach((field) => {
                if (!field.field) return;
                // A repeatable (addRow) field prefills via its Form.List
                // initialValue at mount — setFieldsValue on a Form.List that
                // mounted empty only keeps the last item, so skip it here.
                if (truthy(field.addRow)) return;
                // prefillFromModule redirects this field's source to another
                // module's URL-linked record (e.g. the current job).
                const fieldSource = field.prefillFromModule
                    ? contextRecords[normalizeModule(field.prefillFromModule)]
                    : sourceRecord;
                if (!fieldSource) return;
                // prefillFrom (admin config) → field → payloadKey, dotted or nested.
                const stored = pickPrefillValue(fieldSource, field);
                if (stored === undefined || stored === null) return;
                if (field.type === 'file') {
                    const fl = toFileListValue(stored);
                    if (fl.length > 0) values[field.field] = fl;
                } else {
                    values[field.field] = buildInitialValue(field, stored);
                }
            });
        });
        if (Object.keys(values).length > 0) form.setFieldsValue(values);
    }, [initialRecord, prefillFlatRecord, contextRecords, loading, groups, form]);

    // Linkage merged into the create payload: URL-derived when prefillFromUrl,
    // else the explicit extraPayload prop.
    const scopePayload = hasClientScope && effectiveClientId ? { clientId: effectiveClientId } : {};
    const effectiveExtraPayload = { ...scopePayload, ...(urlPrefill?.linkage ?? extraPayload ?? {}) };

    // True once both the form groups and (when needed) the prefill source data are
    // ready. The Form.List components must NOT mount until this is true so that
    // getAddRowInitialValue below provides the correct initialValue at mount —
    // antd ignores initialValue changes after a Form.List has already mounted.
    const isFormReady = !waitingForClientScope && !loading && (!urlPrefill?.source || (prefillGroups !== null && !prefillLoading));

    // Build the Form.List initialValue for an addRow group. When candidate prefill
    // data is available the group's payloadKey (e.g. "workExperience") is looked up
    // in addRowPrefillsByKey and the stored rows are mapped through buildInitialValue
    // so antd controls receive the right shape. Falls back to a single empty row.
    const getAddRowInitialValue = (group) => {
        const gKey = group.payloadKey || group.name;
        const rows = addRowPrefillsByKey[gKey];
        const minRows = Math.max(0, Number(group.minRows ?? 0) || 0);
        if (!rows || rows.length === 0) {
            // initialRows in config controls how many empty rows to start with (default 0).
            // minRows still controls the minimum kept after removal.
            const startCount = group.initialRows !== undefined
                ? Math.max(minRows, Math.max(0, Number(group.initialRows) || 0))
                : minRows;
            return Array.from({ length: startCount }, () => ({}));
        }
        const initial = rows.map(row => {
            const rowVals = {};
            (group.fields ?? []).forEach(field => {
                if (!field.field || field.type === 'file') return;
                const val = pickPrefillValue(row, field);
                if (val !== undefined && val !== null) {
                    // A repeat field inside the row (nested Form.List) hydrates as
                    // an array, each element normalised for its control; a plain
                    // field hydrates as a single value.
                    rowVals[field.field] = truthy(field.addRow)
                        ? buildRepeatFieldInitial(field, val)
                        : buildInitialValue(field, val);
                }
            });
            return Object.keys(rowVals).length > 0 ? rowVals : {};
        });
        while (initial.length < minRows) initial.push({});
        return initial;
    };

    // Precomputed per-group so a linked set (group.linkGroup) can be padded to
    // equal length BEFORE any Form.List mounts — antd ignores initialValue
    // changes after first mount, so this has to be ready up front, not derived
    // ad hoc per group. A no-op passthrough for every group not in a linked set.
    const addRowInitials = useMemo(() => {
        const raw = {};
        groups.forEach((group) => {
            if (group.addRow) raw[group.name] = getAddRowInitialValue(group);
        });
        return padLinkedInitialValues(linkedGroupSets, raw);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups, addRowPrefillsByKey, linkedGroupSets]);

    const toggle = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

    const handleReset = () => {
        form.resetFields();
        // Also reset collapsed state to default (all expanded)
        setCollapsed({});
        setSelectedSource('internal');
        setTabs([]);
    };

    const handleSearch = useCallback(async (values, sourceOverride, paginationOverride = {}) => {
        if (!isSearch) return;
        const nextPageSize = Number(paginationOverride.pageSize) > 0
            ? Number(paginationOverride.pageSize)
            : searchPagination.pageSize;
        const requestedCurrent = Number(paginationOverride.current);
        const nextCurrent = Number.isFinite(requestedCurrent) && requestedCurrent > 0
            ? Math.floor(requestedCurrent)
            : 1;

        try {
            setLastSearchValues(values);
            setSearchPagination({ current: nextCurrent, pageSize: nextPageSize });
            setSearchResults((current) => ({ ...current, loading: true, searched: true }));
            const checkedSources = getSearchSources(values?.selectedSource);
            const activeSource = sourceOverride
                ? resolveSearchSource(sourceOverride, selectedSource)
                : (checkedSources.includes('internal')
                    ? 'internal'
                    : resolveSearchSource(values?.selectedSource, selectedSource));
            
            // Include selectedSource in the search parameters
            const searchParamsWithSource = {
                ...values,
                selectedSource: activeSource,
            };
            
            console.log('[AddFormV1] Calling searchModuleData with scope:', activeSource);
            
            const result = await searchModuleData(moduleName || 'candidates', searchParamsWithSource, { 
                limit: nextPageSize, 
                offset: (nextCurrent - 1) * nextPageSize,
            }, {
                scope: activeSource,
            });

            setSelectedSource(activeSource);
            setSearchResults({
                searched: true,
                rows: result.rows,
                total: result.total,
                fields: result.fields,
                // Admin-configured row actions/rules — searchModuleData pulls these
                // from the same /module-data-list response the regular ListView
                // list-endpoint returns them on, so search results get the same
                // admin-driven actions instead of none.
                actions: result.actions ?? [],
                actionRules: result.actionRules ?? [],
                loading: false,
            });
            setTabs((currentTabs) => buildSourceTabs({
                sources: checkedSources,
                activeSource,
                activeCount: result.total,
                responseCounts: result.counts,
                currentTabs,
                backendTabs: result.tabs,
            }));
        } catch (error) {
            console.error('Search failed:', error);
            message.error(error?.message || 'Failed to search records');
            setSearchResults({ searched: true, rows: [], total: 0, fields: [], actions: [], actionRules: [], loading: false });
        }
    }, [isSearch, moduleName, selectedSource, searchPagination.pageSize]);

    const handleSourceAction = useCallback(async (action, record) => {
        const actionKey = getSourceActionInstanceKey(action, record);
        setSourceActionState((current) => ({
            ...current,
            [actionKey]: { ...(current[actionKey] ?? {}), loading: true },
        }));
        try {
            const response = await runSourceAction(action, record);
            message.success(
                response?.message
                ?? response?.data?.message
                ?? action.successMessage
                ?? 'Action completed successfully.',
            );
            setSourceActionState((current) => ({
                ...current,
                [actionKey]: { loading: false, completed: true },
            }));
        } catch (error) {
            message.error(error?.message || action.errorMessage || 'Action failed.');
            setSourceActionState((current) => ({
                ...current,
                [actionKey]: { ...(current[actionKey] ?? {}), loading: false },
            }));
        } finally {
            setSourceActionState((current) => ({
                ...current,
                [actionKey]: { ...(current[actionKey] ?? {}), loading: false },
            }));
        }
    }, []);

    const handleTabChange = useCallback((newSource) => {
        const activeSource = resolveSearchSource(newSource, selectedSource);
        setSelectedSource(activeSource);
        setSearchPagination({ current: 1, pageSize: searchPagination.pageSize });
        
        // Re-trigger search with new source if we have previous search values
        if (Object.keys(lastSearchValues).length > 0) {
            handleSearch(lastSearchValues, activeSource, { current: 1, pageSize: searchPagination.pageSize });
        }
    }, [searchPagination.pageSize, lastSearchValues, handleSearch, selectedSource]);

    const handleSearchPageChange = useCallback((current, pageSize) => {
        const nextPageSize = Number(pageSize) > 0 ? Number(pageSize) : searchPagination.pageSize;
        const nextCurrent = Number(current) > 0 ? Number(current) : 1;
        if (Object.keys(lastSearchValues).length === 0) {
            setSearchPagination({ current: nextCurrent, pageSize: nextPageSize });
            return;
        }
        handleSearch(lastSearchValues, selectedSource, { current: nextCurrent, pageSize: nextPageSize });
    }, [handleSearch, lastSearchValues, searchPagination.pageSize, selectedSource]);

    const handleSearchPageSizeChange = useCallback((pageSize) => {
        const nextPageSize = Number(pageSize) > 0 ? Number(pageSize) : searchPagination.pageSize;
        if (Object.keys(lastSearchValues).length === 0) {
            setSearchPagination({ current: 1, pageSize: nextPageSize });
            return;
        }
        handleSearch(lastSearchValues, selectedSource, { current: 1, pageSize: nextPageSize });
    }, [handleSearch, lastSearchValues, searchPagination.pageSize, selectedSource]);

    const submit = async (values) => {
        if (isSearch) {
            await handleSearch(values);
            return;
        }

        if (onSubmit) {
            await onSubmit(values);
            return;
        }

        if (!moduleName) {
            console.log('Form values:', values);
            return;
        }

        try {
            setSubmitLoading(true);
            const response = await createModuleRecord(moduleName, buildDynamicFormData(values, groups, effectiveExtraPayload ?? {}, moduleName));
            const success = response?.status !== false;

            if (!success) {
                message.error(submitErrorMessage(groups, moduleName, 'add',
                    response?.error || response?.message || response?.data));
                return;
            }

            // Repeatable (addRow) document files are attached in a follow-up update
            // through the reliable indexed path (documents[0], documents[1], …). The
            // rows themselves were created on insert (metadata is in the create JSON),
            // so this only uploads each file to its row by index. Runs only when the
            // form actually carried addRow file uploads, so plain records and jobs
            // stay single-request.
            const addRowFileParts = collectFileParts(values, groups, { scope: 'addRow', indexed: true, module: moduleName });
            const newRecordId = response?.id ?? response?.data?.id ?? response?.data?._id ?? response?._id;
            if (addRowFileParts.length > 0 && newRecordId) {
                try {
                    await updateRecord(moduleName, newRecordId, { ...(effectiveExtraPayload ?? {}), ...buildPayload(values, groups, { module: moduleName }) }, addRowFileParts);
                } catch (uploadErr) {
                    console.error('Document file attach (post-create update) failed:', uploadErr);
                    message.warning('Record created, but attaching document files failed. Edit the record to re-upload.');
                }
            }

            // Common document registry — the module record is created FIRST (the
            // Create API above generated its reference id), then every uploaded
            // file (single or multiple, flat or addRow) is registered in the
            // shared "documents" collection against that id, keyed by module name
            // + the form field it was uploaded under (resume, jobDescription, …).
            // Best-effort: the record already exists, so a registry failure only
            // warns and never rolls back the create.
            const allFileParts = collectFileParts(values, groups, { module: moduleName });
            if (allFileParts.length > 0 && newRecordId) {
                try {
                    await uploadModuleDocuments(moduleName, newRecordId, allFileParts);
                } catch (docErr) {
                    console.error('Document registry upload failed:', docErr);
                    message.warning('Record created, but saving its document records failed.');
                }
            }

            // Admin-configurable success toast (group.submitMessages); defaults to
            // "<Module> has added successfully" for every module, new ones included.
            const successText = submitSuccessMessage(groups, moduleName, 'add');
            if (successText) message.success(successText);
            form.resetFields();

            // A clicked extra button (formActions) overrides the group's default
            // afterSubmit for this submit; the plain Create button leaves the ref
            // null and keeps the existing behavior.
            const afterSubmit = pendingAfterSubmitRef.current ?? resolveAfterSubmit(groups);
            if (!afterSubmit) {
                // No admin-configured behavior: preserve existing default navigation.
                const module = normalizeModule(moduleName);
                if (module === 'jobs') navigate('/jobs');
                else if (module === 'candidates') navigate('/candidates');
                else if (module === 'onboarding') navigate('/onboarding');
                else if (module === 'submission') navigate('/submissions');
            } else if (afterSubmit.mode === 'back') {
                navigate(-1);
            } else if (afterSubmit.mode === 'route' || afterSubmit.mode === 'detail') {
                const target = safeNavTarget(afterSubmit.target, newRecordId, values);
                if (target) navigate(target);
            }
            // afterSubmit.mode === 'stay' (or an unresolvable target): no navigation.
        } catch (err) {
            console.error(`Failed to create ${moduleName}:`, err);
            message.error(submitErrorMessage(groups, moduleName, 'add',
                err?.data?.error || err?.data?.message || err?.message));
        } finally {
            setSubmitLoading(false);
            pendingAfterSubmitRef.current = null;
        }
    };

    const getFieldDependencies = (field, currentName) => {
        const rules = field.validations ?? field.validation ?? field.rules ?? [];
        return rules
            .filter((rule) => {
                const type = typeof rule === 'string' ? rule : rule?.type;
                return ['dateAfterField', 'greaterThanField', 'strictGreaterThanField',
                    'strictLessThanField', 'lessThanOrEqualField', 'maxFieldWithFallback',
                    'dateBeforeField', 'timeAfterField', 'timeBeforeField'].includes(type);
            })
            .map((rule) => {
                const compareFields = String(rule.value ?? rule.compareField ?? rule.field ?? '')
                    .split(',').map((item) => item.trim()).filter(Boolean);
                return compareFields.map((compareField) => getSiblingFieldName(currentName, compareField));
            })
            .flat()
            .filter(Boolean);
    };

    const evaluateShowIf = (showIf, getFieldValue, siblingPrefix) => {
        if (!showIf?.field) return true;
        const path = siblingPrefix != null
            ? [...(Array.isArray(siblingPrefix) ? siblingPrefix : [siblingPrefix]), showIf.field]
            : [showIf.field];
        const val = getFieldValue(path);
        switch (showIf.operator) {
            case 'eq': return String(val ?? '') === String(showIf.value ?? '');
            case 'neq': return String(val ?? '') !== String(showIf.value ?? '');
            case 'truthy': return val !== undefined && val !== null && val !== '' && val !== false;
            case 'falsy': return val === undefined || val === null || val === '' || val === false;
            // notEmpty targets an addRow GROUP's own Form.List name (showIf.field is
            // that group's name, e.g. "submission_referral_details") — true once the
            // group has at least one row. Lets a flat field elsewhere in the form
            // (e.g. "referral rate") only appear/require once a referral row exists.
            case 'notEmpty': return Array.isArray(val) ? val.length > 0 : Boolean(val);
            // in/notIn: showIf.value is a comma-separated list (e.g. "W2-Fulltime,W2-Contract").
            case 'in': return String(showIf.value ?? '').split(',').map((v) => v.trim()).includes(String(val ?? ''));
            case 'notIn': return !String(showIf.value ?? '').split(',').map((v) => v.trim()).includes(String(val ?? ''));
            default: return true;
        }
    };

    const [combineToggles, setCombineToggles] = useState({});

    const fieldKey = (field) => String(field?.field ?? '');

    // Who is filling this form — drives admin-configured field autoValueRules
    // (display only; the gateway enforces the same rules server-side).
    const actingIdentity = useMemo(() => getActingIdentity(), []);

    const renderField = (field, namePrefix, listName, opts = {}) => {
        // Split dotted keys into array form so AntD Form nests values correctly:
        // "passport.passportNumber" → ['passport','passportNumber']
        const fieldParts = typeof field.field === 'string' && field.field.includes('.')
            ? field.field.split('.')
            : null;
        const parsedField = fieldParts ?? field.field;
        const name = namePrefix !== undefined
            ? [namePrefix, ...(fieldParts ?? [field.field])]
            : parsedField;
        // Inside a Form.List the relative name [rowIndex, fieldName] is correct for
        // Form.Item registration, but form.getFieldValue / cross-field validators need
        // the absolute path [listName, rowIndex, fieldName] to find the value in the
        // form store. Build that here so validators and validateFields use the right path.
        // listName is normally a single Form.List name (string), but a DOUBLY-nested
        // list (a repeat field's subFields rendered inside an addRow group row) passes
        // the full absolute path array of its own list — spread it instead of wrapping.
        const absoluteName = (listName !== undefined && namePrefix !== undefined)
            ? [...(Array.isArray(listName) ? listName : [listName]), ...(Array.isArray(name) ? name : [name])]
            : name;
        // Checkbox.Group (when options are configured) uses value=[]; single boolean checkbox uses checked=bool.
        const isCheckboxGroup = field.type === 'checkbox' && (
            (field.options && field.options.length > 0) ||
            (field.dataSource && field.dataSource !== 'static')
        );
        const valuePropName = (!isCheckboxGroup && field.type === 'checkbox') ? 'checked' : 'value';
        // Inline (combined) fields show no per-box label — the combined card prints a
        // single label and the boxes sit side by side (e.g. From / To / Currency).
        // hideLabel: the group renders one shared header row above all addRow rows
        // instead of repeating each field's label on every row (e.g. Languages Known).
        const permissionLocked = Boolean(field.editablePermission)
            && !roleAllowsAction(field.editablePermission, moduleName);
        // Sibling path prefix for evaluateShowIf/labelWhen: [listName, rowIndex]
        // in a Form.List, null for ordinary top-level fields.
        const showIfPrefix = (listName !== undefined && namePrefix !== undefined)
            ? [...(Array.isArray(listName) ? listName : [listName]), namePrefix]
            : null;
        // A condition may reference a sibling IN this row, or any top-level
        // field (e.g. contractType). Row prefix only applies when the referenced
        // key is one of this group's own fields; unknown membership (no
        // rowFieldKeys passed) keeps the historical row-first behavior.
        const prefixFor = (conditionKey) => (
            showIfPrefix && (!opts.rowFieldKeys || opts.rowFieldKeys.has(conditionKey))
                ? showIfPrefix
                : null
        );
        const fieldLabel = (opts.inline || opts.hideLabel)
            ? ''
            : (!isCheckboxGroup && field.type === 'checkbox')
                ? ''
                : <ConfiguredFieldLabel form={form} field={field} siblingPrefix={field.labelWhen?.field ? prefixFor(field.labelWhen.field) : showIfPrefix} locked={permissionLocked} />;
        const dependencies = getFieldDependencies(field, absoluteName);
        const mdSpan = opts.inline ? 24 : ((field.type === 'file' && truthy(field.multiple)) || field.type === 'text-editor' ? 24 : (field.col ?? 12));

        // Config-driven default for the Add form: a top-level field (not inside a
        // Form.List) with a configured defaultValue mounts pre-selected. Any prefill
        // (initialRecord / quick-submit) runs through setFieldsValue, which overrides
        // initialValue — so this only affects a fresh Add with no incoming value.
        //
        // An autoValueRule for the acting user (e.g. Submit Source → "External"
        // for an employer-type user) outranks defaultValue: the field mounts
        // showing the value the gateway will enforce anyway. Display only — the
        // backend re-evaluates the same rules and has the final say.
        const autoValue = autoValueFor(field, actingIdentity);
        const initialValue = (namePrefix === undefined && field.type !== 'file'
            && autoValue.matched && autoValue.value !== undefined && autoValue.value !== '')
            ? autoValue.value
            : ((namePrefix === undefined && field.type !== 'file'
                && field.defaultValue !== undefined && field.defaultValue !== '')
                ? field.defaultValue : undefined);

        // AI Actions: admin-configured buttons (Generate/Parse-style) attached to
        // this field. "click" actions render as buttons beside the field; an
        // "upload" action (on a file field) fires automatically when a new file
        // is accepted, via the onAiUpload callback passed into FieldControl.
        const clickAiActions = (field.aiActions || []).filter((a) => a.trigger === 'click');
        const uploadAiAction = (field.aiActions || []).find((a) => a.trigger === 'upload');

        const fieldItem = (
            <Form.Item
                label={fieldLabel}
                name={name}
                extra={field.helpText ? <Text type="secondary" className="v1-field-help-text">{field.helpText}</Text> : undefined}
                required={isFieldRequired(field)}
                rules={getRules(field, form, absoluteName, isSearch)}
                dependencies={dependencies}
                valuePropName={valuePropName}
                initialValue={initialValue}
                getValueFromEvent={field.type === 'file' ? (event) => (Array.isArray(event) ? event : event?.fileList) : undefined}
                validateTrigger={['onBlur', 'onChange']}
            >
                <FieldControl
                    field={field}
                    form={form}
                    name={absoluteName}
                    moduleName={moduleName}
                    scopeValues={{ clientId: effectiveClientId }}
                    onAiUpload={uploadAiAction
                        ? (file) => aiActions.runAction(opts.groupNameForActions, field, uploadAiAction, file)
                        : undefined}
                    formDisabled={aiActions.busy}
                />
            </Form.Item>
        );

        const fieldItemWithActions = clickAiActions.length > 0 ? (() => {
            const positioned = groupActionsByPosition(clickAiActions);
            const group = (position) => (
                <AiActionButtonGroup
                    key={position}
                    position={position}
                    actions={positioned[position]}
                    aiActions={aiActions}
                    groupName={opts.groupNameForActions}
                    field={field}
                />
            );
            return (
                <div className="v1-ai-action-wrap">
                    {group('top-left')}
                    {group('top-center')}
                    {group('top-right')}
                    {fieldItem}
                    {group('bottom-left')}
                    {group('bottom-center')}
                    {group('bottom-right')}
                </div>
            );
        })() : fieldItem;

        const fieldItemWithVerify = field.verifyAction ? (
            <div className="v1-verify-field-row">
                {fieldItemWithActions}
                <VerifyFieldButton form={form} field={field} absoluteName={absoluteName} />
            </div>
        ) : fieldItemWithActions;

        // Inside a combined block, each box's width follows its combineWidth weight
        // (flex-grow), so the pack can be sized by ratio (e.g. 2:1, 1:2:3). Unset → 1.
        const inlineStyle = opts.inline
            ? { flex: `${Number(field.combineWidth) > 0 ? Number(field.combineWidth) : 1} 1 0`, maxWidth: 'none' }
            : undefined;

        const prefillWatcher = field.prefillWhen?.length ? (
            <PrefillWhenWatcher
                key={`${field.field}-prefillWhen`}
                form={form}
                rules={field.prefillWhen}
                watchPath={prefixFor(field.prefillWhen[0].field)
                    ? [...showIfPrefix, field.prefillWhen[0].field]
                    : namePathFromString(field.prefillWhen[0].field)}
                targetPath={absoluteName}
            />
        ) : null;

        if (field.showIf?.field) {
            return (
                <Fragment key={field.field}>
                    {prefillWatcher}
                    <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) =>
                            evaluateShowIf(field.showIf, getFieldValue, prefixFor(field.showIf.field))
                                ? <Col xs={24} md={mdSpan} style={inlineStyle} className={opts.inline ? 'v1-combine-cell' : undefined}>{fieldItemWithVerify}</Col>
                                : null
                        }
                    </Form.Item>
                </Fragment>
            );
        }
        return (
            <Fragment key={field.field}>
                {prefillWatcher}
                <Col xs={24} md={mdSpan} style={inlineStyle} className={opts.inline ? 'v1-combine-cell' : undefined}>{fieldItemWithVerify}</Col>
            </Fragment>
        );
    };

    // renderAddRowField — field-level "addRow": repeats a SINGLE field's control
    // with the same add/remove + min/initial/max config the group-level addRow
    // uses (Form.List), but each list item IS the field's value directly (no
    // sub-key row object). Config keys mirror the group ones: field.minRows /
    // field.initialRows / field.maxRows / field.addLabel.
    //   • minRows    — delete is disabled once the list reaches this count.
    //   • initialRows— empty inputs shown on a fresh Add (falls back to minRows).
    //   • maxRows    — the "Add" button hides once the list reaches this count.
    // Required (field.req) is enforced at the LIST level (>=1 non-empty entry) —
    // per-item rules skip "required" so an empty-but-present item never blocks.
    // Each item renders through singleFieldOf(field) so a repeatable select/
    // multi field shows a SINGLE control per item, not a multi-select.
    // Only used for a top-level field (namePrefix === undefined) — a field with
    // addRow=true inside an addRow GROUP renders as a normal single-value field
    // (no nested repeats).
    const renderAddRowField = (field) => {
        const fieldParts = typeof field.field === 'string' && field.field.includes('.')
            ? field.field.split('.')
            : [field.field];
        const permissionLocked = Boolean(field.editablePermission)
            && !roleAllowsAction(field.editablePermission, moduleName);
        const fieldLabel = <FieldLabel field={field} label={field.label} locked={permissionLocked} />;
        const itemField = singleFieldOf(field);
        // subFields → repeat a GROUP of fields per row (each row is an object of
        // the sub-fields, e.g. question + answer) instead of a single scalar.
        // Rows render through the same renderFields the group-level addRow uses.
        const hasSubFields = Array.isArray(field.subFields) && field.subFields.length > 0;
        // Sub-fields are always shown + editable (the admin exposes no show/edit
        // toggle for them). Force both so a sub-field never renders disabled: the
        // Go FormGroupField serializes show/edit as 0 when unset (no omitempty),
        // which editable() would otherwise read as "not editable".
        const subFieldsToRender = hasSubFields
            ? field.subFields.map((sf) => ({ ...sf, show: 1, edit: 1 }))
            : null;
        const minRows = Math.max(0, Number(field.minRows ?? 0) || 0);
        const maxRows = field.maxRows !== undefined && field.maxRows !== '' && Number(field.maxRows) > 0
            ? Number(field.maxRows) : 0;
        // Prefill source (quick-submit / initialRecord) — the Form.List mounts
        // only once the form is ready, so this initialValue is correct at mount.
        const prefillSource = pickPrefillValue(initialRecord ?? prefillFlatRecord ?? {}, field);
        const initialValue = buildRepeatFieldInitial(field, prefillSource);

        return (
            <Col key={field.field} xs={24} md={24}>
                <Form.Item label={fieldLabel} required={isFieldRequired(field)} colon={false} className="v1-combine-field">
                    <Form.List
                        name={fieldParts}
                        initialValue={initialValue}
                        rules={[{
                            validator: async (_, items = []) => {
                                if (isFieldRequired(field)) {
                                    const filled = hasSubFields
                                        ? items.some((v) => hasMeaningfulValue(v))
                                        : items.some((v) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0));
                                    if (!filled) throw new Error('Mandatory Field');
                                }
                                if (minRows > 0 && items.length < minRows) {
                                    throw new Error(`Keep at least ${minRows} ${hasSubFields ? 'row' : 'value'}${minRows > 1 ? 's' : ''}.`);
                                }
                            },
                        }]}
                    >
                        {(items, { add, remove }, { errors }) => (
                            <div className="v1-addrow-wrapper">
                                {items.map(({ key: itemKey, name: itemName }) => (
                                    <div key={itemKey} className="v1-addrow-item">
                                        <Button
                                            type="text" danger icon={<DeleteOutlined />}
                                            onClick={() => remove(itemName)}
                                            disabled={items.length <= minRows}
                                            className="v1-addrow-delete-btn"
                                        />
                                        <div className="v1-addrow-fields">
                                            {hasSubFields ? (
                                                <Row gutter={[24, 0]} align="top">
                                                    {renderFields(subFieldsToRender, itemName, fieldParts[0], false, field.field)}
                                                </Row>
                                            ) : (
                                                <Form.Item
                                                    name={[itemName]}
                                                    noStyle
                                                    valuePropName={field.type === 'checkbox' && !(field.options?.length) ? 'checked' : 'value'}
                                                    getValueFromEvent={field.type === 'file' ? (event) => (Array.isArray(event) ? event : event?.fileList) : undefined}
                                                    rules={getRules(itemField, form, [...fieldParts, itemName], true)}
                                                >
                                                    <FieldControl
                                                        field={itemField}
                                                        form={form}
                                                        name={[...fieldParts, itemName]}
                                                        moduleName={moduleName}
                                                        scopeValues={{ clientId: effectiveClientId }}
                                                        formDisabled={aiActions.busy}
                                                    />
                                                </Form.Item>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {(!maxRows || items.length < maxRows) && (
                                    <Button type="link" icon={<PlusOutlined />} onClick={() => add()} className="v1-addrow-add-btn">
                                        {field.addLabel ?? `Add ${field.label ?? 'Value'}`}
                                    </Button>
                                )}
                                <Form.ErrorList errors={errors} />
                            </div>
                        )}
                    </Form.List>
                </Form.Item>
            </Col>
        );
    };

    // renderNestedRepeatField — a repeat field (field.addRow) that lives INSIDE
    // an addRow group row. It nests its own Form.List under the current row so
    // every row owns an independent set of repeat values (the payload shape
    // group[rowIndex].repeatField[repeatIndex], e.g. educationDetails[0].subjects).
    // Also handles the field.subFields case (repeat a GROUP of sub-fields
    // together per row, e.g. question + answer) nested the same way — each item
    // renders the sub-fields through the same renderFields the top-level
    // subFields branch (renderAddRowField) uses, so a select/lookup sub-field
    // gets the exact same config-driven dropdown loading as any other field.
    // namePrefix is the outer row's index (from the group Form.List), listName
    // is the group's Form.List name (string) or, for a doubly-nested case, its
    // absolute path array. The nested list's `name` is relative to that list
    // context ([rowIndex, ...fieldParts]); item controls use the absolute path
    // for validators/FieldControl.
    const renderNestedRepeatField = (field, namePrefix, listName) => {
        const fieldParts = typeof field.field === 'string' && field.field.includes('.')
            ? field.field.split('.')
            : [field.field];
        const permissionLocked = Boolean(field.editablePermission)
            && !roleAllowsAction(field.editablePermission, moduleName);
        const fieldLabel = <FieldLabel field={field} label={field.label} locked={permissionLocked} />;
        const itemField = singleFieldOf(field);
        const hasSubFields = Array.isArray(field.subFields) && field.subFields.length > 0;
        const subFieldsToRender = hasSubFields
            ? field.subFields.map((sf) => ({ ...sf, show: 1, edit: 1 }))
            : null;
        const minRows = Math.max(0, Number(field.minRows ?? 0) || 0);
        const maxRows = field.maxRows !== undefined && field.maxRows !== '' && Number(field.maxRows) > 0
            ? Number(field.maxRows) : 0;
        // Relative name (group-list context prefixes the group name); the parent
        // row's initialValue already carries any stored values for this key, so a
        // fresh row falls back to config-driven empty inputs while an edited row
        // inherits its stored values (antd ignores this initialValue when the
        // store already holds a value at this path).
        const listNameRel = [namePrefix, ...fieldParts];
        const nestedAbsoluteListName = [...(Array.isArray(listName) ? listName : [listName]), namePrefix, ...fieldParts];
        const initialValue = buildRepeatFieldInitial(field, undefined);
        return (
            <Col key={`${listName}-${namePrefix}-${field.field}`} xs={24} md={hasSubFields ? 24 : (field.col ?? 24)}>
                <Form.Item label={fieldLabel} required={isFieldRequired(field)} colon={false} className="v1-combine-field">
                    <Form.List
                        name={listNameRel}
                        initialValue={initialValue}
                        rules={hasSubFields ? [{
                            validator: async (_, items = []) => {
                                if (isFieldRequired(field)) {
                                    const filled = items.some((v) => hasMeaningfulValue(v));
                                    if (!filled) throw new Error('Mandatory Field');
                                }
                                if (minRows > 0 && items.length < minRows) {
                                    throw new Error(`Keep at least ${minRows} row${minRows > 1 ? 's' : ''}.`);
                                }
                            },
                        }] : []}
                    >
                        {(items, { add, remove }, { errors }) => (
                            <div className="v1-addrow-wrapper">
                                {items.map(({ key: itemKey, name: itemName }) => {
                                    const absoluteName = [...nestedAbsoluteListName, itemName];
                                    return (
                                        <div key={itemKey} className="v1-addrow-item">
                                            <Button
                                                type="text" danger icon={<DeleteOutlined />}
                                                onClick={() => remove(itemName)}
                                                disabled={items.length <= minRows}
                                                className="v1-addrow-delete-btn"
                                            />
                                            <div className="v1-addrow-fields">
                                                {hasSubFields ? (
                                                    <Row gutter={[24, 0]} align="top">
                                                        {renderFields(subFieldsToRender, itemName, nestedAbsoluteListName, false, field.field)}
                                                    </Row>
                                                ) : (
                                                    <Form.Item
                                                        name={[itemName]}
                                                        noStyle
                                                        valuePropName={field.type === 'checkbox' && !(field.options?.length) ? 'checked' : 'value'}
                                                        getValueFromEvent={field.type === 'file' ? (event) => (Array.isArray(event) ? event : event?.fileList) : undefined}
                                                        rules={getRules(itemField, form, absoluteName, isSearch)}
                                                    >
                                                        <FieldControl
                                                            field={itemField}
                                                            form={form}
                                                            name={absoluteName}
                                                            moduleName={moduleName}
                                                            scopeValues={{ clientId: effectiveClientId }}
                                                            formDisabled={aiActions.busy}
                                                        />
                                                    </Form.Item>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                {(!maxRows || items.length < maxRows) && (
                                    <Button type="link" icon={<PlusOutlined />} onClick={() => add()} className="v1-addrow-add-btn">
                                        {field.addLabel ?? `Add ${field.label ?? 'Value'}`}
                                    </Button>
                                )}
                                <Form.ErrorList errors={errors} />
                            </div>
                        )}
                    </Form.List>
                </Form.Item>
            </Col>
        );
    };

    const renderFields = (fields, namePrefix, listName, hideLabel = false, groupNameForActions) => {
        const used = new Set();
        const out = [];
        // Keys of THIS group's own fields — conditions (showIf/labelWhen/
        // prefillWhen) referencing a field OUTSIDE the group (e.g. the top-level
        // contractType from inside the addRow employer group) must resolve
        // against the top-level form, not the row.
        const rowFieldKeys = new Set(fields.map((f) => f.field).filter(Boolean));
        fields.forEach((field) => {
            if (truthy(field.addRow) && namePrefix === undefined) {
                out.push(renderAddRowField(field));
                return;
            }
            // A repeat field INSIDE an addRow group row (namePrefix defined) gets
            // its own nested Form.List so each row keeps independent repeat values
            // (educationDetails[rowIndex].subjects[]) — including a subFields
            // repeat-GROUP (e.g. question+answer pairs per education row).
            if (truthy(field.addRow) && namePrefix !== undefined) {
                out.push(renderNestedRepeatField(field, namePrefix, listName));
                return;
            }
            const combineKey = field.combineKey?.trim?.();
            if (!combineKey) {
                out.push(renderField(field, namePrefix, listName, { hideLabel, groupNameForActions, rowFieldKeys }));
                return;
            }
            const packKey = `${listName ?? 'root'}:${namePrefix ?? 'root'}:${combineKey}`;
            if (used.has(packKey)) return;
            const pack = fields.filter((f) => f.combineKey === combineKey);
            pack.forEach((f) => used.add(`${listName ?? 'root'}:${namePrefix ?? 'root'}:${f.combineKey}`));

            const labelField = pack.find((f) => f.combineLabel) ?? field;
            const siblingPrefix = (listName !== undefined && namePrefix !== undefined)
                ? [...(Array.isArray(listName) ? listName : [listName]), namePrefix]
                : null;
            const label = <ConfiguredFieldLabel form={form} field={labelField} siblingPrefix={siblingPrefix} combined />;
            const toggleTarget = pack.find((f) => f.combineToggleTarget)?.combineToggleTarget;
            const toggleLabel = pack.find((f) => f.combineToggleLabel)?.combineToggleLabel ?? 'Show range';
            const toggleStateKey = packKey;
            const wrapPack = pack.some((f) => truthy(f.combineWrap));
            const configuredSpan = Number(pack.find((f) => [12, 24].includes(Number(f.combineSpan)))?.combineSpan);
            const targetField = toggleTarget ? pack.find((f) => fieldKey(f) === toggleTarget) : null;
            const targetParts = targetField?.field?.includes?.('.') ? targetField.field.split('.') : [targetField?.field].filter(Boolean);
            const targetName = targetField
                ? ((listName !== undefined && namePrefix !== undefined)
                    ? [listName, namePrefix, ...targetParts]
                    : (targetParts.length > 1 ? targetParts : targetField.field))
                : null;

            // Layout mirrors the legacy combined field: one label, the boxes inline on
            // a single row, and the range toggle BELOW. Toggle off → only the primary
            // (target hidden); toggle on → the target box appears alongside. The wider
            // packs (with currency/unit) take a full row; a simple From/To pair is half.
            const primaryReq = pack.some((f) => isFieldRequired(f));
            const comboSpan = [12, 24].includes(configuredSpan)
                ? configuredSpan
                : (pack.length >= 3 ? 24 : 12);
            out.push(
                <Col key={packKey} xs={24} md={comboSpan}>
                    <Form.Item label={label} required={primaryReq} colon={false} className="v1-combine-field">
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const hasTargetValue = targetName ? !empty(getFieldValue(targetName)) : false;
                                const checked = combineToggles[toggleStateKey] ?? hasTargetValue;
                                const visiblePack = toggleTarget && !checked
                                    ? pack.filter((f) => fieldKey(f) !== toggleTarget)
                                    : pack;
                                return (
                                    <>
                                        <Row
                                            gutter={[8, 0]}
                                            align="top"
                                            wrap={wrapPack}
                                            className={`v1-combine-row${wrapPack ? ' v1-combine-row-wrap' : ''}`}
                                        >
                                            {visiblePack.map((f) => renderField(f, namePrefix, listName, { inline: true }))}
                                        </Row>
                                        {toggleTarget && (
                                            <div className="v1-combine-range">
                                                <Switch
                                                    size="small"
                                                    checked={checked}
                                                    onChange={(next) => {
                                                        if (!next && targetName) {
                                                            form.setFieldValue(targetName, undefined);
                                                            form.validateFields([targetName]).catch(() => { });
                                                        }
                                                        setCombineToggles((prev) => ({ ...prev, [toggleStateKey]: next }));
                                                    }}
                                                />
                                                <Text type="secondary" className="v1-combine-toggle-label">{toggleLabel}</Text>
                                            </div>
                                        )}
                                    </>
                                );
                            }}
                        </Form.Item>
                    </Form.Item>
                </Col>,
            );
        });
        return out;
    };

    // Derive fields from search results for ListView (same logic as JobsListPage)
    const searchFields = useMemo(() => {
        if (searchResults.fields?.length) return searchResults.fields;
        const firstRow = searchResults.rows[0];
        if (!firstRow) return [];

        return Object.keys(firstRow)
            .filter((key) => !key.startsWith('_') && typeof firstRow[key] !== 'object')
            .map((key) => ({
                value: key,
                field: key,
                label: key.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase()),
                isVisible: true,
            }));
    }, [searchResults.fields, searchResults.rows]);

    // Determine view mode based on source type: show list view for external sources,
    // card view for internal sources
    const getViewModeFromRecords = (records) => {
        if (!Array.isArray(records) || records.length === 0) return 'card';
        
        const hasExternalSource = records.some((record) => {
            const sourceFields = [
                record?.sourceType,
                record?.profileSource,
                record?.selectedSource,
                record?.customFields?.sourceType,
                record?.customFields?.profileSource,
            ];
            
            return sourceFields.some((value) => {
                if (value === null || value === undefined) return false;
                const normalized = String(value).trim().toLowerCase();
                if (!normalized) return false;
                return !['all', 'internal'].includes(normalized);
            });
        });
        
        return hasExternalSource ? 'list' : 'card';
    };

    const defaultViewMode = useMemo(() => {
        return getViewModeFromRecords(searchResults.rows);
    }, [searchResults.rows]);

    const searchFieldsWithActions = useMemo(() => {
        const sourceActions = getAvailableSourceActions({
            rows: searchResults.rows,
            selectedSource,
        });
        if (!sourceActions.length) return searchFields;

        const actionFields = new Set(sourceActions.map((action) => action.field));
        const fieldsWithoutActions = searchFields.filter(
            (field) => field.cardRole !== 'action' && !actionFields.has(field.value),
        );
        return [
            ...fieldsWithoutActions,
            ...sourceActions.map((action) => ({
                value: action.field,
                field: action.field,
                label: action.label ?? 'Action',
                cardRole: 'action',
                isVisible: true,
                tableHidden: true,
                sorter: false,
                render: (_value, record) => {
                    if (!sourceActionAppliesToRecord(action, record, selectedSource)) return null;
                    const actionKey = getSourceActionInstanceKey(action, record);
                    const state = sourceActionState[actionKey] ?? {};
                    const missingActionId = !actionKey;
                    const disabled = missingActionId || state.completed;

                    return (
                        <Button
                            type="primary"
                            size="small"
                            loading={state.loading}
                            disabled={disabled}
                            onClick={() => handleSourceAction(action, record)}
                        >
                            {state.completed ? action.completedLabel : action.buttonLabel}
                        </Button>
                    );
                },
            })),
        ];
    }, [handleSourceAction, searchFields, searchResults.rows, selectedSource, sourceActionState]);

    // Merge page-level context (e.g. { jobId }) into every search-result row
    // action's popupState, so a popup it opens (via ListView's action.route
    // handling) receives that context too — see the searchActionContext prop.
    const searchResultActions = useMemo(() => {
        if (!searchActionContext) return searchResults.actions;
        const mergeContext = (action) => ({
            ...action,
            popupState: { ...searchActionContext, ...action.popupState },
            ...(action.children ? { children: action.children.map(mergeContext) } : {}),
        });
        return (searchResults.actions ?? []).map(mergeContext);
    }, [searchResults.actions, searchActionContext]);

    const renderGroup = (group, index) => {
        const key = group.name ?? group.id ?? String(index);
        const groupName = group.name ?? key;
        // group.linkGroup: when this group is linked to others, the LEADER owns
        // the Add button + minRows/requireOne validator for the whole set; every
        // member (leader + followers) still gets its own Form.List/Card/rows, and
        // add/remove always go through every member via listControlsRef so row
        // counts stay equal by construction. Unlinked groups (linkedSet === null,
        // i.e. every group that predates this feature) fall through leaderGroup
        // === group, so minRows/rules/initialValue below compute exactly as before.
        const linkedSet = findLinkedSet(linkedGroupSets, groupName);
        const isFollower = Boolean(linkedSet) && linkedSet.leaderName !== groupName;
        const leaderGroup = linkedSet
            ? (groups.find((g) => g.name === linkedSet.leaderName) ?? group)
            : group;
        const minRows = Math.max(0, Number(leaderGroup.minRows ?? 0) || 0);
        const maxRows = Math.max(0, Number(leaderGroup.maxRows ?? 0) || 0);
        const linkedMemberNames = linkedSet ? linkedSet.memberNames : [groupName];
        const addLinkedRow = () => {
            linkedMemberNames.forEach((name) => listControlsRef.current[name]?.add());
        };
        const removeLinkedRow = (rowName) => {
            linkedMemberNames.forEach((name) => listControlsRef.current[name]?.remove(rowName));
        };
        const body = group.addRow ? (
            <Form.List
                name={groupName}
                initialValue={addRowInitials[groupName] ?? []}
                rules={isFollower ? [] : [
                    {
                        validator: async (_, rows = []) => {
                            // A group switched OFF via its own toggle (e.g. "Fresher" hiding
                            // Work Experience) is not rendered at all — don't force rows on it.
                            const switchedOn = truthy(group.toggleEnabled) && group.toggleField
                                && Boolean(form.getFieldValue(group.toggleField));
                            if (switchedOn) return;
                            if (minRows > 0 && rows.length < minRows) {
                                throw new Error(`Keep at least ${minRows} row${minRows > 1 ? 's' : ''}.`);
                            }
                            if (truthy(group.requireOne)) {
                                const invalid = rows.some((row) => !hasMeaningfulValue(row));
                                if (invalid) throw new Error('Fill at least one field in each added row.');
                            }
                        },
                    },
                ]}
            >
                {(rows, { add, remove }, { errors }) => {
                    listControlsRef.current[groupName] = { add, remove };
                    return (
                        <div className="v1-addrow-wrapper">
                            <div className="v1-addrow-topbar">
                                {!isFollower ? (
                                    (!maxRows || rows.length < maxRows) && (
                                        <Button
                                            type="link"
                                            icon={<PlusOutlined />}
                                            onClick={addLinkedRow}
                                            className="v1-addrow-add-btn"
                                        >
                                            {group.addLabel ?? `Add ${group.label ?? 'Row'}`}
                                        </Button>
                                    )
                                ) : (
                                    <Text type="secondary" className="v1-addrow-linked-hint">
                                        Rows added via {leaderGroup.label ?? leaderGroup.name}
                                    </Text>
                                )}
                            </div>

                            {truthy(group.sharedRowLabels) && rows.length > 0 && (
                                <Row gutter={[24, 0]} className="v1-addrow-header">
                                    {group.fields.map((field) => (
                                        <Col key={fieldKey(field)} md={field.col ?? 12} className="v1-addrow-header-label">
                                            {field.label}
                                        </Col>
                                    ))}
                                </Row>
                            )}

                            {rows.map(({ key: rowKey, name }) => (
                                <div key={rowKey} className="v1-addrow-item">
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => removeLinkedRow(name)}
                                        disabled={rows.length <= minRows}
                                        className="v1-addrow-delete-btn"
                                    />

                                    <div className="v1-addrow-fields">
                                        <Row gutter={[24, 0]} align="top">
                                            {renderFields(group.fields, name, groupName, truthy(group.sharedRowLabels), groupName)}
                                        </Row>
                                    </div>
                                </div>
                            ))}
                            <Form.ErrorList errors={errors} />
                        </div>
                    );
                }}
            </Form.List>
        ) : (
            <Row gutter={[24, 0]}>{renderFields(group.fields, undefined, undefined, false, groupName)}</Row>
        );

        return (
            <Card
                key={key}
                className="v1-card v1-edit-card"
                title={<SectionTitle icon={sectionIcons[index % sectionIcons.length]} title={isSearch ? (searchFormLabel || group.label) : (group.label ?? group.name ?? 'Details')} />}
                extra={(
                    <Space size={8} align="center" className="v1-group-actions">
                        <GroupInfoMessage group={group} />
                        <GroupToggleControl group={group} />
                        <Button
                            type="text"
                            size="small"
                            icon={collapsed[key] ? <DownOutlined /> : <UpOutlined />}
                            onClick={(event) => {
                                event.stopPropagation();
                                toggle(key);
                            }}
                        />
                    </Space>
                )}
            >
                <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                        const switchedOn = truthy(group.toggleEnabled) && group.toggleField && Boolean(getFieldValue(group.toggleField));
                        return <div style={{ display: collapsed[key] || switchedOn ? 'none' : 'block' }}>{body}</div>;
                    }}
                </Form.Item>
            </Card>
        );
    };

    return (
        <main>
            {breadcrumbItems.length > 0 && <AppBreadcrumb items={breadcrumbItems} />}
            <div className="v1-edit-page">
                {hasClientScope && !presetClientId && (
                    <Card className="v1-card v1-edit-card v1-client-gate-card">
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <SectionTitle icon={<UserOutlined />} title="Select Client" />
                            {clientGateMessage && <Text type="secondary">{clientGateMessage}</Text>}
                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={14}>
                                    <Select
                                        showSearch
                                        allowClear
                                        placeholder="Select client"
                                        style={{ width: '100%' }}
                                        value={effectiveClientId || undefined}
                                        optionFilterProp="label"
                                        options={clientOptions.map((c) => ({
                                            value: c._id ?? c.id,
                                            label: c.clientName ?? c.name ?? c.clientRefrenceId ?? c._id,
                                        })).filter((c) => c.value)}
                                        onChange={handleClientGateChange}
                                    />
                                </Col>
                            </Row>
                        </Space>
                    </Card>
                )}
                <div className="v1-page-inner">
                    <Spin
                        spinning={(!isFormReady && !waitingForClientScope) || aiActions.busy}
                        description={aiActions.busy ? 'Running AI action…' : 'Loading form fields...'}
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            colon={false}
                            className="v1-form"
                            disabled={aiActions.busy}
                            requiredMark={(label, { required }) => (
                                <>
                                    {label}
                                    {required && <span className="v1-required-mark"> *</span>}
                                </>
                            )}
                            onFinish={submit}
                            onFinishFailed={(info) => {
                                // Expand collapsed sections first — a hidden error
                                // field can't be scrolled to or focused.
                                setCollapsed({});
                                scrollToFirstFormError(info, form);
                            }}
                        >
                            {isFormReady && groups.map((group, index) => (
                                group.showIf?.field
                                    ? <GroupShowIfGate key={group.name ?? `grp-${index}`} form={form} showIf={group.showIf} render={() => renderGroup(group, index)} />
                                    : renderGroup(group, index)
                            ))}
                            {isFormReady && !error && groups.length === 0 && (
                                <Alert type="info" showIcon message={isSearch ? "No search configuration found." : "No form fields are configured."} />
                            )}
                            {isFormReady && isSearch && (
                                <div className="v1-search-form-actions">
                                    <Space size={12}>
                                        <Button onClick={handleReset} disabled={!groups.length}>
                                            Reset
                                        </Button>
                                        <Button onClick={() => navigate(cancelPath)}>
                                            Back
                                        </Button>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            disabled={submitLoading || !groups.length || searchDisabled}
                                            loading={submitLoading}
                                        >
                                            {submitText}
                                        </Button>
                                    </Space>
                                </div>
                            )}
                        </Form>
                    </Spin>
                </div>

                {!isSearch && (
                    <div className="v1-footer">
                        <Text type="secondary">All Unsaved Changes will be lost if you cancel and reload.</Text>
                        <Space size={12}>
                            <Button onClick={() => navigate(cancelPath)}>Cancel</Button>
                            {formActions.map((action, index) => (
                                <FormActionButton
                                    key={`${action.label ?? 'action'}-${index}`}
                                    form={form}
                                    groups={groups}
                                    action={action}
                                    disabled={!isFormReady || submitLoading || !groups.length}
                                    loading={submitLoading}
                                    onFire={() => {
                                        pendingAfterSubmitRef.current = action.afterSubmit ?? { mode: 'stay' };
                                        form.submit();
                                    }}
                                />
                            ))}
                            <ValidationGatedSubmitButton
                                form={form}
                                groups={groups}
                                onClick={() => { pendingAfterSubmitRef.current = null; form.submit(); }}
                                disabled={!isFormReady || submitLoading || !groups.length}
                                loading={submitLoading}
                            >
                                {submitText}
                            </ValidationGatedSubmitButton>
                        </Space>
                    </div>
                )}

                {/* Search results ListView — rendered inside AddFormV1 when in search mode */}
                {isSearch && searchResults.searched && (
                    <ListView
                        moduleName={moduleName}
                        breadcrumbItems={[]}
                        dataSource={searchResults.rows}
                        fields={searchFieldsWithActions}
                        total={searchResults.total}
                        loading={searchResults.loading}
                        current={searchPagination.current}
                        pageSize={searchPagination.pageSize}
                        showViewToggle
                        activeView={viewMode}
                        onViewChange={setViewMode}
                        onPageChange={handleSearchPageChange}
                        onPageSizeChange={handleSearchPageSizeChange}
                        showSearch={false}
                        tabs={tabs}
                        selectedSource={selectedSource}
                        onTabChange={handleTabChange}
                        actions={searchResultActions}
                        actionRules={searchResults.actionRules}
                    />
                )}
            </div>
        </main>
    );
}
